import React from 'react';

/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useEffect, useContext, useRef, lazy, Suspense, type ReactNode, type FormEvent, type ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate, Outlet, useParams } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { 
  Globe, Clock, MapPin, 
  Smartphone, 
  Cpu, 
  CheckCircle2, 
  ArrowRight, 
  MessageCircle, 
  Menu, 
  X, 
  Zap, 
  Target,
  Shield, 
  Users,
  ExternalLink,
  ChevronRight,
  Mail,
  Phone,
  LayoutDashboard,
  Eye,
  Send,
  Lock,
  LogOut,
  Plus,
  Trash2,
  Heart,
  Utensils,
  ShoppingCart,
  Building2,
  Lightbulb,
  PenTool,
  Rocket,
  Code,
  User as UserIcon,
  Facebook,
  Instagram,
  Video,
  CreditCard,
  Copy,
   Monitor,
  Tablet,
  Play,
  TrendingUp,
  Wrench,
  Bot,
  Sparkles,
  Search,
  RotateCw
} from 'lucide-react';
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  serverTimestamp, 
  updateDoc, 
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  where,
  Timestamp 
} from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import type { User } from 'firebase/auth';
import { db, auth, signInWithGoogle } from './firebase';
import { useTranslation } from 'react-i18next';

const AboutUs = lazy(() => import('./pages/AboutUs').then(m => ({ default: m.AboutUs })));
const Work = lazy(() => import('./pages/Work').then(m => ({ default: m.Work })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const ArticlePage = lazy(() => import('./pages/ArticlePage').then(m => ({ default: m.ArticlePage })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const Services = lazy(() => import('./pages/Services'));
const WebDesign = lazy(() => import('./pages/WebDesign'));
const WebsiteDevelopment = lazy(() => import('./pages/WebsiteDevelopment'));
const BusinessWebsites = lazy(() => import('./pages/BusinessWebsites'));
const Ecommerce = lazy(() => import('./pages/Ecommerce'));
const SEOPage = lazy(() => import('./pages/SEOPage'));
const WebApplications = lazy(() => import('./pages/WebApplications'));
import { blogContent } from './data/blogContent';
import { findArticle, getCanonicalArticleUrl, getArticleHreflangs, getCanonicalArticlePath } from './data/blogUtils';
import { localizedRoutesMap } from './routes';
import { BrandsStrip, SelectedProjects, WhyChooseUs, Process, Insights } from './components/HomeSections';
import { StarBackground } from './components/StarBackground';

import LocalSEO from './pages/LocalSEO';
import SoftwareDevelopment from './pages/SoftwareDevelopment';
import Branding from './pages/Branding';
import AiSolutions from './pages/AiSolutions';

import { FlagSQ, FlagEN, FlagDE } from './components/FlagIcons';


// --- Types ---
enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface Message {
  id: string;
  name: string;
  surname: string;
  username: string;
  email: string;
  text: string;
  screenshot?: string;
  createdAt: Timestamp;
  replied?: boolean;
  replyText?: string;
  read?: boolean;
}

interface Project {
  id: string;
  title: string;
  url: string;
  thumbnail: string;
  description: string;
  category: 'web' | 'mobile' | 'ai';
  projectType?: 'live' | 'demo';
  ownerId?: string;
  ownerName?: string;
  likes?: string[];
  seoScore?: number;
  views?: number;
  createdAt: Timestamp;
}

interface Review {
  id: string;
  projectId: string;
  userId: string;
  userName: string;
  rating: number;
  comment: string;
  createdAt: Timestamp;
}

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  role: 'admin' | 'client';
  plan: 'free' | 'launch' | 'growth' | 'enterprise';
  planExpiresAt?: Timestamp;
  status?: 'active' | 'inactive';
  createdAt: Timestamp;
}

type Page = 'home' | 'tracker' | 'admin' | 'payment' | 'profile' | 'publish' | 'privacy' | 'terms' | 'refund';

interface Plan {
  name: string;
  price: string;
}




// --- Components ---
export const SEO = ({ title, description, keywords, canonical, includeSchema = false, pageKey, noindex = false, serviceType }: { title?: string, description?: string, keywords?: string, canonical?: string, includeSchema?: boolean, pageKey?: string, noindex?: boolean, serviceType?: string }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [globalSeo, setGlobalSeo] = useState<any>(null);

  useEffect(() => {
    import('firebase/firestore').then(({ doc, getDoc }) => {
      getDoc(doc(db, 'site_settings', 'seo')).then((snap) => {
        if (snap.exists()) setGlobalSeo(snap.data());
      }).catch(() => {});
    });
  }, []);
  
  const siteTitle = globalSeo?.defaultTitle || t('seo.defaultTitle');
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDesc = globalSeo?.defaultDescription || t('seo.homeDesc');
  const finalDesc = description || defaultDesc;
  
  const fallbackKeywords = t('seo.homeKeywords') || "web design, mobile apps, AI solutions, software development, Kosovo, Albania";
  const finalKeywords = [keywords, globalSeo?.targetKeywords, fallbackKeywords].filter(Boolean).join(', ');
  
  const url = globalSeo?.canonicalUrl || "https://www.vizionidigjital.com";
  const finalRobots = noindex ? "noindex, nofollow" : (globalSeo?.robotsRule || "index, follow");
  const finalOgImage = globalSeo?.ogImage || `${url}/og-banner.png`;

  // Determine current language strictly from the URL path first
  const currentRouteLang: 'sq' | 'en' | 'de' = location.pathname.startsWith('/de')
    ? 'de'
    : location.pathname.startsWith('/en')
    ? 'en'
    : 'sq';

  // Find current page key
  let currentPageKey = pageKey;
  let currentArticleSlug = '';
  if (!currentPageKey) {
    for (const lang of Object.keys(localizedRoutesMap)) {
      for (const [key, path] of Object.entries(localizedRoutesMap[lang])) {
        if (path.includes(':title')) {
          const basePath = path.split('/:title')[0];
          if (location.pathname.startsWith(basePath + '/')) {
            currentPageKey = key;
            currentArticleSlug = decodeURIComponent(location.pathname.replace(basePath + '/', ''));
            break;
          }
        } else if (location.pathname === path || location.pathname + '/' === path || location.pathname === path + '/') {
          currentPageKey = key;
          break;
        }
      }
      if (currentPageKey) break;
    }
  }

  const hreflangs: { lang: string; url: string }[] = [];
  if (currentPageKey === 'articles' && currentArticleSlug) {
    const matched = findArticle(currentArticleSlug);
    if (matched) {
      hreflangs.push(...getArticleHreflangs(matched.id, url));
    }
  } else if (currentPageKey) {
    for (const lang of Object.keys(localizedRoutesMap)) {
      const alternatePath = localizedRoutesMap[lang][currentPageKey];
      if (alternatePath) {
        hreflangs.push({ lang, url: `${url}${alternatePath === '/' ? '' : alternatePath}` });
      }
    }
    const defaultEnPath = localizedRoutesMap.en[currentPageKey];
    if (defaultEnPath) {
      hreflangs.push({ lang: 'x-default', url: `${url}${defaultEnPath === '/' ? '' : defaultEnPath}` });
    }
  }

  // Compute canonical URL safely: ALWAYS self-referencing to the current page on www.vizionidigjital.com
  let canonicalUrl = `${url}${location.pathname === '/' ? '' : location.pathname.replace(/\/+$/, '')}`;
  if (canonical) {
    canonicalUrl = canonical.startsWith('http') ? canonical : `${url}${canonical === '/' ? '' : canonical.replace(/\/+$/, '')}`;
  } else if (currentPageKey === 'articles' && currentArticleSlug) {
    const matched = findArticle(currentArticleSlug);
    if (matched) {
      canonicalUrl = getCanonicalArticleUrl(matched.id, currentRouteLang, url);
    }
  } else if (currentPageKey && localizedRoutesMap[currentRouteLang]?.[currentPageKey]) {
    const p = localizedRoutesMap[currentRouteLang][currentPageKey];
    canonicalUrl = `${url}${p === '/' ? '' : p}`;
  }


  // Structured Data schemas array
  const schemas: any[] = [];
  if (includeSchema) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "Organization"],
      "@id": `${url}/#organization`,
      "name": "Vizioni Digjital",
      "url": url,
      "logo": `${url}/favicon.svg`,
      "image": `${url}/og-banner.png`,
      "telephone": "+38349558803",
      "email": "vizionidigjital@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Suhodoll",
        "addressLocality": "Mitrovica",
        "addressCountry": "Kosovo"
      },
      "sameAs": [
        "https://www.facebook.com/vizionidigjital",
        "https://www.instagram.com/vizionidigjital"
      ],
      "areaServed": {
        "@type": "Country",
        "name": "Kosovo"
      }
    });
    if (serviceType) {
      schemas.push({
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": serviceType,
        "provider": {
          "@id": `${url}/#organization`
        },
        "areaServed": [
          { "@type": "AdministrativeArea", "name": "Global" },
          { "@type": "AdministrativeArea", "name": "Kosovo" },
          { "@type": "AdministrativeArea", "name": "Albania" }
        ]
      });
    }

    // 2.5 Breadcrumbs
    if (currentPageKey && currentPageKey !== 'home') {
      const currentLang = i18n.language.split('-')[0] || 'sq';
      const homeName = currentLang === 'en' ? 'Home' : (currentLang === 'de' ? 'Startseite' : 'Ballina');
      
      const breadcrumbList = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": homeName,
            "item": `${url}${localizedRoutesMap[currentLang].home === '/' ? '' : localizedRoutesMap[currentLang].home}`
          }
        ]
      };

      const isServicePage = ['webDesign', 'websiteDevelopment', 'businessWebsites', 'ecommerce', 'seo', 'webApplications'].includes(currentPageKey);
      
      if (isServicePage) {
        const servicesName = currentLang === 'en' ? 'Services' : (currentLang === 'de' ? 'Dienstleistungen' : 'Shërbimet');
        breadcrumbList.itemListElement.push({
          "@type": "ListItem",
          "position": 2,
          "name": servicesName,
          "item": `${url}${localizedRoutesMap[currentLang].services}`
        });
        
        breadcrumbList.itemListElement.push({
          "@type": "ListItem",
          "position": 3,
          "name": title || serviceType || '',
          "item": canonicalUrl
        });
      } else {
        breadcrumbList.itemListElement.push({
          "@type": "ListItem",
          "position": 2,
          "name": title || '',
          "item": canonicalUrl
        });
      }

      schemas.push(breadcrumbList);
    }

    // 3. WebSite schema
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${url}/#website`,
      "url": url,
      "name": "Vizioni Digjital",
      "publisher": {
        "@id": `${url}/#organization`
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${url}/blog?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    });

    // 4. FAQ Schema for Page (Homepage target)
    if (location.pathname === '/' || location.pathname === '' || location.pathname === '/sq' || location.pathname === '/en' || location.pathname === '/de') {
      const isEn = i18n.language.startsWith('en');
      const isDe = i18n.language.startsWith('de');
      
      const q1 = isEn ? "How long does it take to build a professional website in Kosovo?" : (isDe ? "Wie lange dauert es, eine professionelle Website im Kosovo zu erstellen?" : "Sa zgjat ndërtimi i një uebfaqeje profesionale në Kosovë?");
      const a1 = isEn ? "Building a professional website with Vizioni Digjital usually takes 1 to 2 weeks for standard presentation projects, and up to 4 weeks for more complex solutions or e-commerce stores." : (isDe ? "Die Erstellung einer professionellen Website mit Vizioni Digjital dauert in der Regel 1 bis 2 Wochen für Standard-Präsentationsprojekte und bis zu 4 Wochen für komplexere Lösungen oder E-Commerce-Shops." : "Ndërtimi i një uebfaqeje profesionale nga Vizioni Digjital zgjat zakonisht 1 deri në 2 javë për projekte prezantuese standarde, dhe deri në 4 javë për zgjidhje më komplekse ose dyqane online e-commerce.");
      
      const q2 = isEn ? "Do you offer Google SEO optimization?" : (isDe ? "Bieten Sie Google SEO-Optimierung an?" : "A ofroni optimizim SEO për Google?");
      const a2 = isEn ? "Yes, basic technical SEO optimization is integrated for free in all our packages to ensure your site is indexed quickly and has the maximum chance of ranking high in Prishtina, Kosovo and the region." : (isDe ? "Ja, die grundlegende technische SEO-Optimierung ist in allen unseren Paketen kostenlos integriert, um sicherzustellen, dass Ihre Website schnell indexiert wird und maximale Chancen auf ein hohes Ranking in Prishtina, im Kosovo und in der Region hat." : "Po, optimizimi teknik SEO bazë është i integruar falas në të gjitha paketave tona për të siguruar që faqja juaj të indeksohet shpejt dhe të ketë shanset maksimale për t'u renditur lart në Prishtinë, Kosovë dhe rajon.");

      const q3 = isEn ? "How can a client pay for a project upgrade or integration?" : (isDe ? "Wie kann ein Kunde für ein Projekt-Upgrade bezahlen?" : "Si mund të bëjë pagesën një klient për përmirësim apo upgrade projekti?");
      const a3 = isEn ? "Clients can easily make payments for special requests or integrations directly through our pricing page in the Project Upgrade Fee option, fully supported and secured by Stripe." : (isDe ? "Kunden können problemlos Zahlungen für spezielle Anfragen oder Integrationen direkt über unsere Preisseite in der Option Project Upgrade Fee vornehmen, die vollständig von Stripe unterstützt und gesichert wird." : "Klientët mund të kryejnë me lehtësi pagesa për integrime apo kërkesa të veçanta direkt përmes faqes sonë të çmimeve në opsionin Project Upgrade Fee, i cili mbështetet dhe sigurohet plotësisht nga Stripe.");

      const q4 = isEn ? "Will my website work perfectly on mobile phones?" : (isDe ? "Wird meine Website auf Mobiltelefonen einwandfrei funktionieren?" : "A do të punojë uebfaqja ime perfekte në telefona mobilë?");
      const a4 = isEn ? "Every project we build is fully responsive and optimized for high speed on mobile, tablet, and desktop according to official Google Core Web Vitals guidelines." : (isDe ? "Jedes von uns entwickelte Projekt ist vollständig responsiv und nach offiziellen Google Core Web Vitals-Richtlinien für hohe Geschwindigkeiten auf Mobilgeräten, Tablets und Desktops optimiert." : "Çdo projekt i ndërtuar nga ne është plotësisht i përshtatshëm (100% responsive) dhe i optimizuar për shpejtësi të lartë në celular, tablet dhe desktop sipas udhëzuesve zyrtarë të Google Core Web Vitals.");

      schemas.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": q1, "acceptedAnswer": { "@type": "Answer", "text": a1 } },
          { "@type": "Question", "name": q2, "acceptedAnswer": { "@type": "Answer", "text": a2 } },
          { "@type": "Question", "name": q3, "acceptedAnswer": { "@type": "Answer", "text": a3 } },
          { "@type": "Question", "name": q4, "acceptedAnswer": { "@type": "Answer", "text": a4 } }
        ]
      });
    }
  }

  // 4. Dynamic Article / BlogPosting Schema for Article Routes
  if (currentPageKey === 'articles' && currentArticleSlug) {
    const matched = findArticle(currentArticleSlug);
    if (matched) {
      const foundArticle = matched.data[currentRouteLang] || matched.data.en;
      schemas.push({
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "@id": `${canonicalUrl}/#post`,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalUrl
        },
        "headline": foundArticle.title,
        "description": description || (foundArticle.content[0]?.text?.substring(0, 160) || defaultDesc),
        "image": foundArticle.image || `${url}/og-banner.png`,
        "datePublished": "2026-05-10",
        "dateModified": new Date().toISOString().split('T')[0],
        "author": {
          "@type": "Organization",
          "name": "Vizioni Digjital",
          "url": url
        },
        "publisher": {
          "@type": "Organization",
          "name": "Vizioni Digjital",
          "logo": {
            "@type": "ImageObject",
            "url": `${url}/favicon.svg`
          }
        }
      });
    }
  }

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDesc} />
      <meta name="keywords" content={keywords || fallbackKeywords} />
      
      <link rel="canonical" href={canonicalUrl} />
      {hreflangs.map((h, i) => (
        <link key={i} rel="alternate" hrefLang={h.lang} href={h.url} />
      ))}
      {hreflangs.length > 0 && (
        <link rel="alternate" hrefLang="x-default" href={hreflangs.find(h => h.lang === 'en')?.url || hreflangs[0].url} />
      )}

      
      {/* AI Bot and SEO Directives for New Search Engines */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <meta name="aibot" content="index, follow, snippet" />
      <meta property="article:tag" content="Vizioni Digjital" />
      <meta property="article:tag" content="Premium Web Design" />
      <meta property="article:tag" content="AI Software Solutions" />
      <meta property="article:tag" content="Custom App Development" />
      
      {/* Geo Location Tags for Local SEO */}
      <meta name="geo.region" content="XK" />
      <meta name="geo.placename" content="Prishtina" />
      <meta name="geo.position" content="42.8913;20.8660" />
      <meta name="ICBM" content="42.8913, 20.8660" />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDesc} />
      <meta property="og:image" content={`${url}/og-banner.png`} />
      <meta property="og:image:secure_url" content={`${url}/og-banner.png`} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="Vizioni Digjital" />
      <meta property="og:type" content="website" />
      
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || defaultDesc} />
      <meta name="twitter:image" content={`${url}/og-banner.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@vizionidigjital" />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

const Logo = ({ className = "w-10 h-10" }: { className?: string }) => (
  <img 
    src="/favicon.svg" 
    alt="Vizioni Digjital Logo" 
    className={className} 
  />
);

const handleFirestoreError = (error: unknown, operationType: OperationType, path: string | null) => {
  const errInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  // In a real app, we might show a toast or alert here
};

const calculateSeoScore = (project: { title: string, description?: string, url?: string, thumbnail: string }) => {
  let score = 50;
  if (project.title && project.title.length > 5) score += 15;
  if (project.description && project.description.length > 20) score += 20;
  if (project.url && project.url.includes('https')) score += 10;
  if (project.thumbnail) score += 5;
  return Math.min(score, 100);
};

const ScrollProgress = () => {
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let ticking = false;
    let totalHeight = document.documentElement.scrollHeight - window.innerHeight;
    
    const handleResize = () => {
      totalHeight = document.documentElement.scrollHeight - window.innerHeight;
    };
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (barRef.current) {
            const currentProgress = totalHeight > 0 ? (window.scrollY / totalHeight) * 100 : 0;
            barRef.current.style.width = `${currentProgress}%`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 z-[100] bg-white/5 pointer-events-none">
      <div ref={barRef} className="h-full bg-primary-light shadow-[0_0_15px_rgba(139,92,246,0.6)] transition-none" style={{ width: '0%' }} />
    </div>
  );
};

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = i18n.language.split('-')[0].toUpperCase();

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng.toLowerCase());
    
    // Find the current page key
    let currentPageKey = 'home';
    for (const l of Object.keys(localizedRoutesMap)) {
      for (const [key, path] of Object.entries(localizedRoutesMap[l])) {
        if (path.includes(':title')) {
           const basePath = path.split('/:title')[0];
           if (location.pathname.startsWith(basePath)) {
              currentPageKey = key;
              break;
           }
        } else if (location.pathname === path || location.pathname + '/' === path || location.pathname === path + '/') {
           currentPageKey = key;
           break;
        }
      }
    }
    
    // Navigate to the localized route for the selected language
    let newPath = localizedRoutesMap[lng.toLowerCase()][currentPageKey];
    if (newPath) {
      if (newPath.includes(':title')) {
        const currentTitleSegment = decodeURIComponent(location.pathname.split('/').pop() || '');
        const matched = findArticle(currentTitleSegment);
        const targetLang = lng.toLowerCase() as 'sq' | 'en' | 'de';
        const targetSlug = (matched && matched.data[targetLang]?.slug) || currentTitleSegment;
        newPath = newPath.replace(':title', targetSlug);
      }
      navigate(newPath);
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          const userDocRef = doc(db, 'users', u.uid);
          const userDoc = await getDoc(userDocRef);
          if (!userDoc.exists()) {
            await setDoc(userDocRef, {
              email: u.email,
              displayName: u.displayName,
              photoURL: u.photoURL,
              createdAt: serverTimestamp(),
              role: 'user', // default role
              plan: 'free'
            });
            // Trigger Welcome Email on first ever sign in!
            fetch('/api/email/welcome', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: u.email, displayName: u.displayName })
            }).catch(console.error);
          }
        } catch (e) {
          console.error("Error creating user profile", e);
        }
      }
    });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      unsubscribe();
    };
  }, []);

  const handleNavClick = (sectionId: string) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#1a1a1a] border-b border-white/5 py-2 px-6 hidden lg:block">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-xs font-bold text-white/80">
          <div className="flex gap-6">
            <a href="mailto:vizionidigjital@gmail.com" className="flex items-center gap-2 hover:text-primary-light transition-colors"><Mail size={12}/> vizionidigjital@gmail.com</a>
            <a href="tel:+38349558803" className="flex items-center gap-2 hover:text-primary-light transition-colors"><Phone size={12}/> +383 49 558 803</a>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://www.facebook.com/profile.php?id=61588797729975" aria-label="Facebook" target="_blank" rel="noreferrer" className="hover:text-primary-light transition-colors flex items-center gap-1"><Facebook size={12} /> Facebook</a>
            <a href="https://www.instagram.com/vizionidigjital/" aria-label="Instagram" target="_blank" rel="noreferrer" className="hover:text-primary-light transition-colors flex items-center gap-1"><Instagram size={12} /> Instagram</a>
            <a href="https://www.tiktok.com/@vizioni.digjital" aria-label="TikTok" target="_blank" rel="noreferrer" className="hover:text-primary-light transition-colors flex items-center gap-1"><Video size={12} /> TikTok</a>
          </div>
        </div>
      </div>

      <nav className={`fixed left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'top-0 lg:top-8 bg-near-black/80 backdrop-blur-md py-4 border-b border-white/5' : 'top-0 lg:top-8 bg-transparent py-4 md:py-6'}`}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.home || '/'} className="flex items-center gap-2 cursor-pointer">
          <Logo className="w-10 h-10" />
          <span className="font-display font-bold text-xl tracking-tighter">VIZIONI<span className="text-primary-light">DIGJITAL</span></span>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center gap-6">
          <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.services || "/sq/sherbimet"} className="text-xs font-bold hover:text-primary-light transition-colors uppercase tracking-widest">{t('nav.services')}</Link>
          <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.templates || "/sq/shabllone"} className="text-xs font-bold hover:text-primary-light transition-colors uppercase tracking-widest">{t('nav.tracker')}</Link>
          <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.pricing || "/sq/cmimet"} className="text-xs font-bold hover:text-primary-light transition-colors uppercase tracking-widest">{t('nav.pricing')}</Link>
          <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.work || "/sq/puna"} className="text-xs font-bold hover:text-primary-light transition-colors uppercase tracking-widest">{t('nav.work')}</Link>
          <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.about || "/sq/rreth-nesh"} className="text-xs font-bold hover:text-primary-light transition-colors uppercase tracking-widest">{t('nav.about')}</Link>
          <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.contact || "/sq/kontakt"} className="text-xs font-bold hover:text-primary-light transition-colors uppercase tracking-widest">{t('nav.contact')}</Link>
          <div className="flex items-center gap-3 ml-4">
            {user ? (
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.profile || "/sq/profili"} className="flex items-center gap-2 group">
                <img src={user.photoURL || ''} className="w-8 h-8 rounded-full border border-white/10 group-hover:border-primary-light transition-all" alt="" loading="lazy" />
                <span className="text-xs font-bold uppercase tracking-widest group-hover:text-primary-light transition-colors">{user.displayName?.split(' ')[0]}</span>
              </Link>
            ) : (
              <button onClick={signInWithGoogle} className="text-xs font-bold uppercase tracking-widest hover:text-primary-light transition-colors">{t('common.login')}</button>
            )}
            <div className="flex items-center gap-1 bg-white/5 border border-white/10 hover:border-white/20 transition-colors rounded-full p-1 shadow-sm">
              <button 
                onClick={() => changeLanguage('sq')} 
                title="Shqip" 
                aria-label="Gjuha Shqipe"
                className={`relative p-1 rounded-full transition-all duration-300 ${currentLang === 'SQ' ? 'ring-2 ring-primary-light bg-white/15 scale-105 shadow-[0_0_10px_rgba(139,92,246,0.5)] opacity-100' : 'opacity-50 hover:opacity-100 hover:scale-105'}`}
              >
                <FlagSQ className="w-4 h-3 rounded-[2px] shadow-sm object-cover block" />
              </button>
              <button 
                onClick={() => changeLanguage('en')} 
                title="English" 
                aria-label="English language"
                className={`relative p-1 rounded-full transition-all duration-300 ${currentLang === 'EN' ? 'ring-2 ring-primary-light bg-white/15 scale-105 shadow-[0_0_10px_rgba(139,92,246,0.5)] opacity-100' : 'opacity-50 hover:opacity-100 hover:scale-105'}`}
              >
                <FlagEN className="w-4 h-3 rounded-[2px] shadow-sm object-cover block" />
              </button>
              <button 
                onClick={() => changeLanguage('de')} 
                title="Deutsch" 
                aria-label="Deutsche Sprache"
                className={`relative p-1 rounded-full transition-all duration-300 ${currentLang === 'DE' ? 'ring-2 ring-primary-light bg-white/15 scale-105 shadow-[0_0_10px_rgba(139,92,246,0.5)] opacity-100' : 'opacity-50 hover:opacity-100 hover:scale-105'}`}
              >
                <FlagDE className="w-4 h-3 rounded-[2px] shadow-sm object-cover block" />
              </button>
            </div>
            <button 
              onClick={() => navigate('/pricing')}
              className="btn-primary px-6 py-2 rounded-full text-sm font-bold hover:scale-105 transition-transform active:scale-95 shadow-[0_0_25px_rgba(139,92,246,0.3)]"
            >
              {t('nav.start')}
            </button>
          </div>
        </div>

        {/* Mobile Menu Toggle */}
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden text-white p-2"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          title={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          <span className="sr-only">{isMobileMenuOpen ? "Close menu" : "Open menu"}</span>
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-near-black border-b border-white/5 overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.services || "/sq/sherbimet"} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold hover:text-primary-light transition-colors uppercase tracking-widest text-left">{t('nav.services')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.templates || "/sq/shabllone"} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold hover:text-primary-light transition-colors uppercase tracking-widest text-left">{t('nav.tracker')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.pricing || "/sq/cmimet"} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold hover:text-primary-light transition-colors uppercase tracking-widest text-left">{t('nav.pricing')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.work || "/sq/puna"} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold hover:text-primary-light transition-colors uppercase tracking-widest text-left">{t('nav.work')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.about || "/sq/rreth-nesh"} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold hover:text-primary-light transition-colors uppercase tracking-widest text-left">{t('nav.about')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.contact || "/sq/kontakt"} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-bold hover:text-primary-light transition-colors uppercase tracking-widest text-left">{t('nav.contact')}</Link>
              
              <div className="h-px bg-white/5 my-2" />
              
              <div className="flex items-center justify-between">
                {user ? (
                  <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.profile || "/sq/profili"} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3">
                    <img src={user.photoURL || ''} className="w-10 h-10 rounded-full border border-white/10" alt="" loading="lazy" />
                    <span className="font-bold uppercase tracking-widest text-primary-light">{user.displayName}</span>
                  </Link>
                ) : (
                  <button onClick={() => { signInWithGoogle(); setIsMobileMenuOpen(false); }} className="text-lg font-bold uppercase tracking-widest text-primary-light">{t('common.login')}</button>
                )}
                
                <div className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full p-1 shadow-sm">
                  <button 
                    onClick={() => changeLanguage('sq')} 
                    title="Shqip" 
                    aria-label="Gjuha Shqipe"
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all duration-300 ${currentLang === 'SQ' ? 'ring-2 ring-primary-light bg-white/15 text-primary-light font-bold opacity-100' : 'opacity-60 hover:opacity-100 text-white/80'}`}
                  >
                    <FlagSQ className="w-4 h-3 rounded-[2px] shadow-sm object-cover block" />
                    <span className="text-xs">SQ</span>
                  </button>
                  <button 
                    onClick={() => changeLanguage('en')} 
                    title="English" 
                    aria-label="English language"
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all duration-300 ${currentLang === 'EN' ? 'ring-2 ring-primary-light bg-white/15 text-primary-light font-bold opacity-100' : 'opacity-60 hover:opacity-100 text-white/80'}`}
                  >
                    <FlagEN className="w-4 h-3 rounded-[2px] shadow-sm object-cover block" />
                    <span className="text-xs">EN</span>
                  </button>
                  <button 
                    onClick={() => changeLanguage('de')} 
                    title="Deutsch" 
                    aria-label="Deutsche Sprache"
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full transition-all duration-300 ${currentLang === 'DE' ? 'ring-2 ring-primary-light bg-white/15 text-primary-light font-bold opacity-100' : 'opacity-60 hover:opacity-100 text-white/80'}`}
                  >
                    <FlagDE className="w-4 h-3 rounded-[2px] shadow-sm object-cover block" />
                    <span className="text-xs">DE</span>
                  </button>
                </div>
              </div>

              <button 
                onClick={() => { setIsMobileMenuOpen(false); navigate('/pricing'); }}
                className="w-full btn-primary py-4 rounded-xl text-lg font-bold shadow-[0_0_25px_rgba(139,92,246,0.3)]"
              >
                {t('nav.start')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
    </>
  );
};

const ChatSystem = ({ isOpen, setIsOpen, profile }: { isOpen: boolean, setIsOpen: (o: boolean) => void, profile: UserProfile | null }) => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<'contact' | 'ai'>('ai');
  const [formData, setFormData] = useState({ name: '', surname: '', username: '', email: '', text: '' });
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'success'>('idle');
  const [aiMessages, setAiMessages] = useState<{ role: 'user' | 'ai', text: string }[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);
  const [showNotification, setShowNotification] = useState(true);
  const chatT = t('chat', { returnObjects: true }) as any;
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const quickReplies = i18n.language.startsWith('sq') 
    ? ["Shërbimet tona", "Sa kushtojnë planet?", "Si të filloj një projekt?"]
    : ["Our services", "How much are the plans?", "How to start a project?"];

  const handleQuickReply = (text: string) => {
    setAiInput(text);
    // Use a small timeout so the state update is reflected before sending
    setTimeout(() => {
      sendAiMessage(text);
    }, 100);
  };

  useEffect(() => {
    scrollToBottom();
  }, [aiMessages, isAiTyping]);

  useEffect(() => {
    if (isOpen) {
      setShowNotification(false);
      localStorage.setItem('vizioni_chat_opened', 'true');
      if (aiMessages.length === 0) {
        const lang = i18n.language || navigator.language.split('-')[0];
        const firstName = profile?.displayName.split(' ')[0] || '';
        
        const welcomeText = lang === 'sq' 
          ? `Përshëndetje ${firstName}! 👋 Unë jam Vizioni AI, asistenti juaj virtual. Si mund t'ju ndihmoj sot?` 
          : `Hello ${firstName}! 👋 I am Vizioni AI, your virtual assistant. How can I help you today?`;

        setIsAiTyping(true);
        setTimeout(() => {
          setAiMessages([{ role: 'ai', text: welcomeText }]);
          setIsAiTyping(false);
        }, 400);
      }
    }
  }, [isOpen, profile]);

  useEffect(() => {
    // Pre-warm the chat backend
    fetch('/api/chat', { method: 'GET' }).catch(() => {});
    
    const hasOpened = localStorage.getItem('vizioni_chat_opened');
    if (hasOpened) setShowNotification(false);
  }, []);

  useEffect(() => {
    if (profile) {
      const names = profile.displayName.split(' ');
      setFormData(prev => ({
        ...prev,
        name: names[0] || '',
        surname: names.slice(1).join(' ') || '',
        email: profile.email,
        username: profile.displayName.toLowerCase().replace(/\s+/g, '_')
      }));
    } else {
      const saved = localStorage.getItem('vizioni_user_data');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setFormData(prev => ({ ...prev, ...parsed }));
        } catch (e) {
          console.error('Error parsing saved data', e);
        }
      }
    }
  }, [profile]);

  const updateFormData = (updates: Partial<typeof formData>) => {
    const newData = { ...formData, ...updates };
    setFormData(newData);
    const { name, surname, username, email } = newData;
    localStorage.setItem('vizioni_user_data', JSON.stringify({ name, surname, username, email }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshot(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await addDoc(collection(db, 'messages'), {
        ...formData,
        screenshot,
        createdAt: serverTimestamp(),
        replied: false
      });
      
      fetch('/api/email/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          surname: formData.surname,
          email: formData.email,
          text: formData.text
        })
      }).catch(console.error);

      setStatus('success');
      setFormData(prev => ({ ...prev, text: '' }));
      setScreenshot(null);
      setTimeout(() => {
        setStatus('idle');
        setIsOpen(false);
      }, 3000);
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'messages');
      setStatus('idle');
    }
  };

  const sendAiMessage = async (overrideInput?: string) => {
    const input = overrideInput || aiInput;
    if (typeof input !== 'string' || !input.trim()) return;
    const userMsg = input;
    if (!overrideInput) setAiInput('');
    setAiMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setIsAiTyping(true);
    
    try {
      // Ensure history strictly starts with user message and alternates properly
      const rawHistory = [...aiMessages, { role: 'user', text: userMsg }];
      const firstUserIndex = rawHistory.findIndex(m => m.role === 'user');
      const filteredHistory = firstUserIndex !== -1 ? rawHistory.slice(firstUserIndex) : rawHistory;

      const contents = filteredHistory.map(m => ({
        role: m.role === 'ai' ? 'model' : 'user',
        parts: [{ text: m.text }]
      }));

      const systemInstruction = `You are "Vizioni AI", the official virtual assistant for Vizioni Digjital, a premium digital agency in Kosovo and Albania.
          
CONTEXT:
---
${document.body.innerText.substring(0, 4000).replace(/\s+/g, ' ')}
---

YOUR ROLE & RULES:
1. Provide extremely clear, short, and concise responses. Keep answers elegant, informative, but very brief (maximum 1 to 2 sentences).
2. For pricing, refer to the "Çmimet" (Pricing) section in the context.
3. LANGUAGE: If the user inputs in Albanian, respond in Albanian. If English, respond in English. Always match the conversational language.
4. If you don't have specific info or if they want custom services, suggest they use the "Na shkruani" (Contact) tab to get in touch with our founders.
5. No fluff or robotic filler phrases. Speak with standard high-level business integrity.
6. LINK POLICY: If you include any links in your responses, they MUST ONLY point to the current website (https://www.vizionidigjital.com) or its official subpages (e.g. '/services', '/work', '/pricing', '/about', '/contact'). DO NOT mention, reference, or link to any other external website or third-party service under any circumstances.
7. LINK FORMAT: Whenever you mention a page or link, use the clean current domain format like \`https://www.vizionidigjital.com/pricing\` or \`https://www.vizionidigjital.com/contact\`. Under NO circumstances should you provide links to other domains.
8. NEVER explain things at length. Give a highly direct, ultra-short reply of 1 sentence (max 2 sentences) instantly. Do not say hello/goodbye in every message if the conversation is ongoing; just answer the user's question directly.`;

      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents, systemInstruction })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Chat request failed");

      const reply = data.text;
      if (!reply) throw new Error("Empty response from AI");
      
      setAiMessages(prev => [...prev, { role: 'ai', text: reply }]);
    } catch (e: any) {
      console.error("AI Chat Error:", e);
      let errorMessage = 'Më vjen keq, pati një problem me shërbimin AI. Te lutem provo pas pak.';
      if (e.message) {
        if (e.message.includes("API key not valid")) {
           errorMessage = "Çelësi i API-së së Google Gemini është i pasaktë ose i pavlefshëm. Ju lutemi verifikoni çelësin në Settings.";
        } else if (e.message.includes("API key")) {
           errorMessage = e.message;
        }
      }
      setAiMessages(prev => [...prev, { role: 'ai', text: errorMessage }]);
    } finally {
      setIsAiTyping(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 md:bottom-8 md:right-8 z-50">
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, y: 20, scale: 0.95, transformOrigin: 'bottom right' }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="absolute bottom-24 right-0 w-[calc(100vw-3rem)] md:w-[420px] h-[600px] max-h-[85vh] overflow-hidden glass-card rounded-3xl shadow-[0_30px_60px_rgba(0,0,0,0.6)] border border-white/10 flex flex-col bg-near-black/95 backdrop-blur-2xl"
          >
             {/* Header */}
             <div className="p-5 border-b border-white/10 flex justify-between items-center bg-gradient-to-r from-primary/10 to-transparent">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center border border-primary/30 rotate-3">
                      <Bot className="text-primary-light" size={24} />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-near-black flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                       Vizioni AI <span className="text-[10px] bg-primary/20 text-primary-light px-2 py-0.5 rounded-full border border-primary/20">BETA</span>
                    </h3>
                    <p className="text-[10px] text-white/80 uppercase tracking-widest font-medium">{chatT.badgeAi || "Asistent Digjital"}</p>
                  </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="w-10 h-10 rounded-full hover:bg-white/5 flex items-center justify-center text-white/80 hover:text-white transition-all">
                  <X size={20} />
                </button>
             </div>

             {/* Navigation Tabs */}
             <div className="flex bg-white/5 p-1.5 mx-5 mt-5 rounded-2xl border border-white/5">
                <button 
                  className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'ai' ? 'bg-primary text-white shadow-[0_5px_15px_rgba(139,92,246,0.4)]' : 'text-white/80 hover:text-white/80'}`}
                  onClick={() => setActiveTab('ai')}
                >
                  <Sparkles size={14} /> {chatT.aiTab || "AI Support"}
                </button>
                <button 
                  className={`flex-1 py-2.5 text-xs font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'contact' ? 'bg-primary text-white shadow-[0_5px_15px_rgba(139,92,246,0.4)]' : 'text-white/80 hover:text-white/80'}`}
                  onClick={() => setActiveTab('contact')}
                >
                  <Send size={14} /> {chatT.contactTab || "Na shkruani"}
                </button>
             </div>

            <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
              {activeTab === 'contact' ? (
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-5"
                >
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {!profile && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-[10px] text-white/80 uppercase font-black tracking-widest ml-1">{chatT.name}</label>
                            <input required className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm focus:border-primary-light outline-none transition-all placeholder:text-white/80" value={formData.name} onChange={e => updateFormData({ name: e.target.value })} />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-[10px] text-white/80 uppercase font-black tracking-widest ml-1">{chatT.surname}</label>
                            <input required className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm focus:border-primary-light outline-none transition-all" value={formData.surname} onChange={e => updateFormData({ surname: e.target.value })} />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-[10px] text-white/80 uppercase font-black tracking-widest ml-1">{chatT.email}</label>
                          <input required type="email" className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm focus:border-primary-light outline-none transition-all" value={formData.email} onChange={e => updateFormData({ email: e.target.value })} />
                        </div>
                      </div>
                    )}
                    {profile && (
                      <div className="bg-primary/5 p-4 rounded-2xl border border-primary/20 mb-2 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden border border-white/10">
                          {profile.photoURL ? <img src={profile.photoURL} alt="" /> : <UserIcon size={24} />}
                        </div>
                        <div>
                          <p className="text-[10px] text-white/80 uppercase tracking-widest font-black">{chatT.sentAs}</p>
                          <p className="text-sm font-bold text-primary-light">{profile.displayName}</p>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <label className="text-[10px] text-white/80 uppercase font-black tracking-widest ml-1">{chatT.msg}</label>
                      <textarea required rows={5} className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 text-sm focus:border-primary-light outline-none resize-none transition-all" value={formData.text} onChange={e => setFormData({...formData, text: e.target.value})} />
                    </div>
                    <button disabled={status === 'sending'} className="w-full py-4.5 bg-primary text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-[0_15px_30px_rgba(139,92,246,0.3)] hover:shadow-[0_20px_40px_rgba(139,92,246,0.5)] transition-all disabled:opacity-50 active:scale-[0.97] mt-2">
                       {status === 'sending' ? chatT.sending : status === 'success' ? chatT.success : chatT.send}
                    </button>
                  </form>
                </motion.div>
              ) : (
                <div className="h-full flex flex-col animate-in fade-in duration-500">
                  <div className="flex-1 space-y-4 pb-4 overflow-y-auto pr-2 custom-scrollbar">
                    {aiMessages.map((m, i) => (
                      <motion.div 
                        key={i} 
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[85%] p-4 rounded-2xl text-sm leading-relaxed shadow-sm ${
                          m.role === 'user' 
                          ? 'bg-primary text-white rounded-tr-none' 
                          : 'bg-white/10 text-white/90 rounded-tl-none border border-white/5 ring-1 ring-white/5'
                        }`}>
                          {m.text}
                        </div>
                      </motion.div>
                    ))}
                    {isAiTyping && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="flex justify-start"
                      >
                        <div className="bg-white/10 p-4 rounded-2xl rounded-tl-none flex gap-1.5 items-center">
                          <div className="w-2 h-2 bg-primary-light rounded-full animate-bounce [animation-delay:-0.3s]" />
                          <div className="w-2 h-2 bg-primary-light rounded-full animate-bounce [animation-delay:-0.15s]" />
                          <div className="w-2 h-2 bg-primary-light rounded-full animate-bounce" />
                        </div>
                      </motion.div>
                    )}
                    
                    {!isAiTyping && aiMessages.length > 0 && aiMessages.length < 5 && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-wrap gap-2 pt-2"
                      >
                        {quickReplies.map((reply, idx) => (
                          <button 
                            key={idx}
                            onClick={() => handleQuickReply(reply)}
                            className="bg-primary/10 hover:bg-primary/20 border border-primary/30 text-primary-light text-[11px] px-3 py-1.5 rounded-full transition-all active:scale-95"
                          >
                             {reply}
                          </button>
                        ))}
                      </motion.div>
                    )}
                    
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="pt-4 border-t border-white/5">
                    <div className="relative group">
                      <input 
                        className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 pr-16 text-sm focus:border-primary-light outline-none transition-all group-hover:bg-white/[0.08]" 
                        placeholder={chatT.placeholder || "Pyet psh: 'Sa kushton plani Growth?'"}
                        value={aiInput} 
                        onChange={e => setAiInput(e.target.value)} 
                        onKeyPress={e => e.key === 'Enter' && sendAiMessage()} 
                      />
                      <button 
                        onClick={() => sendAiMessage()} 
                        disabled={!aiInput.trim() || isAiTyping}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 bg-primary text-white rounded-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:grayscale shadow-lg shadow-primary/20"
                      >
                        <Send size={20} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showNotification && !isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute bottom-20 right-0 w-64 bg-white p-4 rounded-2xl rounded-br-none shadow-2xl border border-primary/20 pointer-events-none"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Bot className="text-primary" size={16} />
              </div>
              <p className="text-xs text-near-black font-medium leading-relaxed">
                {i18n.language.startsWith('sq') 
                  ? "Përshëndetje! 👋 Unë jam asistentja juaj AI. Si mund t'ju ndihmoj sot?" 
                  : i18n.language.startsWith('de')
                  ? "Hallo! 👋 Ich bin Ihr KI-Assistent. Wie kann ich Ihnen heute helfen?"
                  : "Hello! 👋 I'm your AI assistant. How can I help you today?"}
              </p>
            </div>
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white rotate-45 border-r border-b border-primary/20" />
          </motion.div>
        )}
      </AnimatePresence>
      
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="group relative w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center shadow-[0_15px_35px_rgba(139,92,246,0.4)] hover:shadow-[0_20px_45px_rgba(139,92,246,0.6)] hover:scale-110 transition-all active:scale-90"
        aria-label={isOpen ? "Close chat" : "Open chat"}
        title={isOpen ? "Close chat" : "Open chat"}
      >
        {showNotification && !isOpen && (
          <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-[11px] font-black rounded-full flex items-center justify-center border-3 border-near-black shadow-xl z-10">
            !
          </span>
        )}
        
        <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-pulse opacity-20" />
        
        {isOpen ? <X size={28} /> : (
          <div className="relative">
            <MessageCircle size={28} className="group-hover:scale-110 transition-transform" />
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-primary" />
          </div>
        )}
      </button>
    </div>
  );
};


const LeadMagnetBanner = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem('leadMagnetDismissed')) {
        setIsVisible(true);
      }
    }, 30000); // 30 seconds
    return () => clearTimeout(timer);
  }, []);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-0 left-0 right-0 z-[60] btn-primary p-4 md:p-6 shadow-[0_0_30px_rgba(139,92,246,0.2)] flex flex-col md:flex-row items-center justify-between gap-4"
      >
        <div className="flex-1 text-center md:text-left">
          <h3 className="font-bold text-lg mb-1">{t('leadMagnet.title')}</h3>
          <p className="opacity-80 text-sm max-w-2xl">{t('leadMagnet.desc')}</p>
        </div>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              sessionStorage.setItem('leadMagnetDismissed', 'true');
              setIsVisible(false);
              navigate('/contact');
            }}
            className="bg-near-black text-primary-light px-6 py-2 rounded-full font-bold whitespace-nowrap hover:bg-near-black/90 transition-colors"
          >
            {t('leadMagnet.cta')}
          </button>
          <button 
            onClick={() => {
              sessionStorage.setItem('leadMagnetDismissed', 'true');
              setIsVisible(false);
            }}
            className="p-2 hover:bg-black/10 rounded-full transition-colors"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const LegalPage = ({ title, content, type }: { title: string, content: string, type: string }) => {
  const { t } = useTranslation();
  return (
    <section className="pt-32 pb-24 px-6 min-h-screen">
      <SEO 
        title={title} 
        description={t('seo.legalDesc', { title })}
        keywords={`${type}, vizioni digjital ${type}, legal, privacy policy, terms condition, refund`}
        
      />
      <div className="max-w-4xl mx-auto glass-card p-8 md:p-12 rounded-3xl">
        <h1 className="text-4xl font-display font-bold mb-8 text-primary-light">{title}</h1>
        <div 
          className="text-white/80 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </section>
  );
};

const HomePage = ({ setIsChatOpen, setSelectedPlan }: { setIsChatOpen: (o: boolean) => void, setSelectedPlan: (p: Plan) => void }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const heroT = t('hero', { returnObjects: true }) as any;
  const usecasesT = t('usecases', { returnObjects: true }) as any;
  const processT = t('process', { returnObjects: true }) as any;
  const whyT = t('why', { returnObjects: true }) as any;
  const pricingT = t('pricing', { returnObjects: true }) as any;

  return (
    <>
      <SEO 
        description={t('seo.homeDesc')}
        keywords={t('seo.homeKeywords')}
        includeSchema={true}
      />
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <motion.div 
            animate={{ 
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary-light/10 rounded-full blur-[100px]" 
          />
          <motion.div 
            animate={{ 
              x: [0, -40, 0],
              y: [0, 40, 0],
            }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            className="absolute bottom-[-15%] right-[-10%] w-[50%] h-[50%] bg-soft-gold/15 rounded-full blur-[120px]" 
          />
          <motion.div 
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.05, 0.1, 0.05],
            }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[20%] left-[30%] w-[30%] h-[30%] bg-white/5 rounded-full blur-[80px]" 
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }} 
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-primary-light text-xs font-bold uppercase tracking-widest mb-4">{heroT.badge}</p>
            <h1 className="text-4xl leading-[1.1] sm:text-5xl md:text-7xl font-display font-extrabold mb-4 md:mb-6">
              {heroT.title.split(' ').map((word: string, i: number) => 
                ['Digjitale', 'Digital', 'Kunden'].includes(word) 
                  ? <span key={i} className="text-primary-light font-bold glow-text">{word} </span> 
                  : word + ' '
              )}
            </h1>
            <p className="text-sm md:text-xl text-white/80 max-w-xl mb-8 md:mb-10 leading-relaxed">{heroT.sub}</p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-12">
              <button 
                onClick={() => navigate('/contact')}
                className="bg-primary hover:bg-primary-light text-white px-8 py-4 rounded-lg font-bold text-sm md:text-lg shadow-[0_0_25px_rgba(139,92,246,0.4)] transition-all flex items-center justify-center w-full sm:w-auto"
              >
                {t('hero.cta1_start')}
              </button>
              <button 
                onClick={() => navigate('/work')}
                className="border border-white/20 hover:bg-white/10 px-8 py-4 rounded-lg font-bold text-sm md:text-lg transition-all flex items-center justify-center gap-3 w-full sm:w-auto"
              >
                {t('hero.cta2_work')} <div className="w-6 h-6 border border-white/40 rounded-full flex items-center justify-center"><Play size={10} className="ml-[2px]" fill="currentColor" /></div>
              </button>
            </div>
            <p className="text-white/80 text-xs md:text-sm">{heroT.trustLine}</p>
          </motion.div>
          <div className="relative hidden md:block w-full max-w-[400px] md:max-w-[600px] h-[350px] md:h-[550px] mx-auto perspective-1000 group">
            {/* Background Glows and Neon Lines */}
            <div className="absolute inset-0 z-0">
               {/* Animated Neon Rings */}
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full border border-blue-500/20 rounded-[40%] animate-[spin_15s_linear_infinite] shadow-[0_0_30px_rgba(59,130,246,0.3)] pointer-events-none"></div>
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] border border-primary-light/30 rounded-[35%] animate-[spin_20s_linear_infinite_reverse] shadow-[0_0_40px_rgba(167,139,250,0.4)] pointer-events-none mix-blend-screen"></div>
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] border-t-2 border-l-2 border-primary-light/50 rounded-full animate-[spin_10s_linear_infinite] shadow-[0_0_50px_rgba(139,92,246,0.5)] pointer-events-none"></div>

               {/* Ambient Glows */}
               <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/40 rounded-full blur-[80px] pointer-events-none"></div>
               <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-blue-500/30 rounded-full blur-[60px] pointer-events-none"></div>
            </div>

            {/* Rock Base */}
            <div className="absolute bottom-[-10%] left-[-10%] w-[120%] h-[350px] bg-[url('https://images.unsplash.com/photo-1525926477800-7a3eaaafdb88?q=60&w=640&auto=format&fit=crop')] bg-cover bg-left-bottom [mask-image:linear-gradient(to_bottom,transparent_0%,black_40%,black_70%,transparent_100%)] bg-no-repeat opacity-90 mix-blend-luminosity z-10 translate-y-10 rounded-[100%] pointer-events-none shadow-[0_-20px_50px_rgba(0,0,0,0.8)] grayscale contrast-125"></div>
            
            {/* 3D Laptop */}
            <div className="relative z-20 w-[95%] mx-auto mt-0 transform group-hover:scale-[1.03] group-hover:-translate-y-4 transition-all duration-700 ease-in-out drop-shadow-[0_40px_40px_rgba(0,0,0,0.8)]">
               
               <div className="w-[85%] mx-auto relative perspective-[2000px]">
                 {/* Laptop Screen (Lid) */}
                 <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] rounded-t-3xl p-2 border-t border-l border-r border-[#666] shadow-[0_-10px_30px_rgba(0,0,0,0.8),inset_0_0_0_1px_rgba(255,255,255,0.05)] overflow-hidden flex flex-col justify-between z-10 bg-gradient-to-b from-[#222] to-[#0a0a0a]">
                    {/* Screen Content - Mini Website Replica */}
                    <div className="w-full h-full bg-[#050505] relative overflow-hidden rounded-xl border border-[#222]">
                       <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/30 via-transparent to-transparent opacity-80"></div>
                       
                       <div className="p-5 flex flex-col items-start justify-center h-full relative z-10 w-[70%]">
                         <p className="text-[7px] text-primary-light font-bold uppercase tracking-widest mb-2 opacity-80">{heroT.badge}</p>
                         <h2 className="text-[20px] leading-[1.1] font-display font-medium text-white mb-2">{heroT.title}</h2>
                         
                         <div className="flex gap-2 mt-4">
                           <button className="bg-primary hover:bg-primary-light px-4 py-1.5 text-[8px] rounded-lg text-white font-bold transition-all shadow-[0_0_10px_rgba(139,92,246,0.5)] whitespace-nowrap">{t('hero.cta1_start')}</button>
                           <button className="border border-white/20 px-4 py-1.5 text-[8px] rounded-lg text-white font-bold transition-all whitespace-nowrap">{t('hero.cta2_work')}</button>
                         </div>
                       </div>

                       {/* Floating UI Cards */}
                       <div className="absolute right-[-5%] top-[15%] w-[45%] h-[70%] flex gap-2 perspective-[1000px] pointer-events-none">
                          <div className="w-1/2 h-[110%] bg-charcoal rounded-xl border border-white/10 transform [transform:rotateY(-20deg)_rotateZ(5deg)_translateY(8px)] shadow-[0_10px_20px_rgba(0,0,0,0.5)] flex flex-col items-center overflow-hidden">
                            <div className="w-full h-8 bg-near-black border-b border-white/5 flex items-center px-2 gap-1">
                               <div className="w-1.5 h-1.5 rounded-full bg-red-500/50"></div><div className="w-1.5 h-1.5 rounded-full bg-yellow-500/50"></div><div className="w-1.5 h-1.5 rounded-full bg-green-500/50"></div>
                            </div>
                            <img src="https://images.unsplash.com/photo-1600132806370-bf17e65e942f?q=60&w=320&auto=format&fit=crop" className="w-full h-full object-cover opacity-70 mix-blend-luminosity" alt="" loading="lazy"/>
                          </div>
                          <div className="w-1/2 h-[90%] bg-near-black rounded-xl border border-white/10 transform [transform:rotateY(-10deg)_rotateZ(-5deg)_translateY(24px)] shadow-[0_10px_20px_rgba(0,0,0,0.5)] flex items-center justify-center overflow-hidden">
                            <img src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?q=60&w=320&auto=format&fit=crop" className="w-full h-full object-cover opacity-80" alt="" loading="lazy"/>
                          </div>
                       </div>
                       
                       {/* Camera Notch */}
                       <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[12%] h-[12px] bg-[#0a0a0a] rounded-b-lg border-b border-white/5"></div>
                    </div>
                    {/* Screen reflection */}
                    <div className="absolute top-0 left-0 w-[150%] h-[150%] bg-gradient-to-br from-white/10 via-transparent to-transparent transform rotate-[25deg] -translate-x-1/4 -translate-y-1/4 pointer-events-none mix-blend-overlay"></div>
                 </div>

                 {/* Laptop Keyboard Base */}
                 <div className="relative w-[130%] ml-[-15%] h-[90px] mt-[-2px] bg-[#d1d5db] z-0" style={{ clipPath: 'polygon(12% 0, 88% 0, 100% 100%, 0% 100%)', borderRadius: '0 0 20px 20px' }}>
                    <div className="absolute inset-0 bg-gradient-to-b from-[#a3a6aa] via-[#8a8d91] to-[#6b6e73]"></div>
                    {/* Keyboard Track */}
                    <div className="absolute top-[15%] left-1/2 -translate-x-1/2 w-[65%] h-[40%] bg-[#1a1c20] rounded shadow-[inset_0_5px_15px_rgba(0,0,0,1)] border border-[#000]" style={{ clipPath: 'polygon(5% 0, 95% 0, 100% 100%, 0% 100%)' }}></div>
                    {/* Trackpad */}
                    <div className="absolute bottom-[10%] left-1/2 -translate-x-1/2 w-[25%] h-[25%] bg-[#6b6e73] rounded-md shadow-[inset_0_2px_5px_rgba(0,0,0,0.5)] border border-[#444]" style={{ clipPath: 'polygon(5% 0, 95% 0, 100% 100%, 0% 100%)' }}></div>
                 </div>
                 
                 {/* Laptop Front Edge (Thickness) */}
                 <div className="relative w-[130%] ml-[-15%] h-[8px] bg-gradient-to-r from-[#555] via-[#a3a6aa] to-[#333] rounded-b-[20px] shadow-[0_30px_50px_rgba(0,0,0,0.9)] z-0 mt-[-1px]"></div>
               </div>
            </div>
          </div>
        </div>
      </section>
      <SelectedProjects />
      <WhyChooseUs />
      <Process />
      <Insights />

      {/* SOCIAL PROOF SECTION */}
      <section className="py-20 border-t border-white/5 bg-white/[0.01]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
             <h2 className="text-3xl md:text-5xl font-display font-bold tracking-tighter mb-4">{t('testimonials.title')}</h2>
             <p className="text-white/80 text-xl font-light max-w-2xl mx-auto">{t('testimonials.sub')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {(t('testimonials.items', { returnObjects: true }) as any[]).map((testimonial, i) => (
              <div key={i} className="bg-white/5 p-8 rounded-3xl border border-white/10 hover:border-primary-light/30 transition-all relative">
                <div className="text-primary-light mb-4 flex gap-1">
                  {[1,2,3,4,5].map(star => <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>)}
                </div>
                <p className="text-white/80 italic mb-6 leading-relaxed relative z-10">"{testimonial.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center overflow-hidden">
                    <UserIcon color="#8b5cf6" size={24} />
                  </div>
                  <div>
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-xs text-white/80 uppercase tracking-widest">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      
      <section className="py-24 bg-charcoal/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-4">{t('faqs.title')}</h2>
            <p className="text-white/80">{t('faqs.sub')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {(t('faqs.items', { returnObjects: true }) as any[]).map((faq, i) => (
              <div key={i} className="glass-card p-6 rounded-2xl border border-white/5">
                <h3 className="font-bold mb-3 text-primary-light">{faq.q}</h3>
                <p className="text-sm text-white/80 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </>
  );
};

const TrackerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [filter, setFilter] = useState<'all' | 'web' | 'mobile' | 'ai'>('all');
  const [projectTypeFilter, setProjectTypeFilter] = useState<'all' | 'live' | 'demo'>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'popular' | 'seo'>('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [devicePreview, setDevicePreview] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [iframeLoading, setIframeLoading] = useState(true);

  const [iframeKey, setIframeKey] = useState(Date.now());

  useEffect(() => {
    if (selectedProject) setIframeLoading(true);
  }, [selectedProject, iframeKey]);
  const trackerT = t('tracker', { returnObjects: true }) as any;
  const location = useLocation();

  useEffect(() => {
    const q = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedProjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project));
      setProjects(fetchedProjects);
      
      // Auto-open project if specified in URL
      const searchParams = new URLSearchParams(location.search);
      const urlProjectId = searchParams.get('id');
      if (urlProjectId) {
        const found = fetchedProjects.find(p => p.id === urlProjectId);
        if (found) {
          setSelectedProject(found);
        }
      }
    });
    return () => unsubscribe();
  }, [location.search]);

  useEffect(() => {
    if (selectedProject) {
      // We query without orderBy to avoid needing a composite index in Firestore for "projectId" + "createdAt".
      // Then we sort the results on the client side.
      const q = query(
        collection(db, 'reviews'), 
        where('projectId', '==', selectedProject.id)
      );
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const fetchedReviews = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Review));
        // Sort client side: newest first
        fetchedReviews.sort((a, b) => {
          const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return timeB - timeA;
        });
        setReviews(fetchedReviews);
      });
      return () => unsubscribe();
    } else {
      setReviews([]);
    }
  }, [selectedProject]);

  const handleLike = async (projectId: string, likes: string[] = []) => {
    if (!auth.currentUser) return setShowAuthPopup(true);
    const uid = auth.currentUser.uid;
    const newLikes = likes.includes(uid) ? likes.filter(id => id !== uid) : [...likes, uid];
    try {
      await updateDoc(doc(db, 'projects', projectId), { likes: newLikes });
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddReview = async (e: FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser || !selectedProject) return setShowAuthPopup(true);
    if (!newReview.comment.trim()) return;

    try {
      await addDoc(collection(db, 'reviews'), {
        projectId: selectedProject.id,
        userId: auth.currentUser.uid,
        userName: auth.currentUser.displayName || t('common.anonymous'),
        userPhoto: auth.currentUser.photoURL || '',
        rating: newReview.rating,
        comment: newReview.comment,
        createdAt: serverTimestamp()
      });
      setNewReview({ rating: 5, comment: '' });
      setShowReviewForm(false);
    } catch (e) {
      console.error(e);
    }
  };

  const sortedProjects = [...projects]
    .filter(p => {
      const matchesCategory = filter === 'all' || p.category === filter;
      const computedType = p.projectType || 'live';
      const matchesType = projectTypeFilter === 'all' || computedType === projectTypeFilter;
      const matchesSearch = searchQuery.trim() === '' || 
        p.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
        (p.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.category.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesType && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'popular') return (b.likes?.length || 0) - (a.likes?.length || 0);
      if (sortBy === 'seo') return (b.seoScore || 0) - (a.seoScore || 0);
      return b.createdAt.toMillis() - a.createdAt.toMillis();
    });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": sortedProjects.map((project, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "SoftwareApplication",
        "name": project.title,
        "image": project.thumbnail,
        "description": project.description || `${project.title} template at Vizioni Digjital`,
        "applicationCategory": project.category,
        "url": project.url
      }
    }))
  };

  const renderReviews = (className?: string) => (
    <div className={`space-y-6 ${className || ''}`}>
      <div className="flex justify-between items-center">
        <h4 className="font-bold text-white text-sm">{trackerT.reviews} <span className="text-white/80">({reviews.length})</span></h4>
        {!showReviewForm && (
          <button 
            onClick={() => setShowReviewForm(true)}
            className="text-xs font-bold text-primary-light hover:text-white transition-colors bg-primary/10 px-3 py-1.5 rounded-full border border-primary/20"
          >
            + {trackerT.addReview}
          </button>
        )}
      </div>

      {showReviewForm ? (
        <motion.form 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          onSubmit={handleAddReview} 
          className="p-5 bg-white/5 rounded-2xl border border-white/10 space-y-4 shadow-inner"
        >
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] text-white/80 uppercase font-black tracking-widest">{trackerT.rating}</label>
              <span className="text-[10px] font-bold text-yellow-400">{newReview.rating}/5 Yje</span>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map(num => (
                <button 
                  key={num}
                  type="button"
                  onClick={() => setNewReview({...newReview, rating: num})}
                  className={`text-2xl transition-all hover:scale-125 ${num <= newReview.rating ? 'text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.4)]' : 'text-white/10 grayscale'}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <textarea 
            required
            placeholder={trackerT.commentPlaceholder}
            className="w-full bg-near-black border border-white/10 rounded-xl p-4 text-sm outline-none focus:border-primary-light resize-none placeholder:text-white/10 min-h-[100px]"
            value={newReview.comment}
            onChange={e => setNewReview({...newReview, comment: e.target.value})}
          />
          <div className="flex gap-2 pt-2">
            <button type="submit" className="flex-grow btn-primary py-3 rounded-xl font-bold text-xs uppercase tracking-widest shadow-xl">{trackerT.send}</button>
            <button type="button" onClick={() => setShowReviewForm(false)} className="px-5 bg-white/5 py-3 rounded-xl font-bold text-xs uppercase tracking-widest text-white/80 hover:text-white transition-colors">{trackerT.cancel}</button>
          </div>
        </motion.form>
      ) : null}

      <div className="space-y-4">
        {reviews.map(review => (
          <motion.div 
            key={review.id} 
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white/[0.02] p-5 rounded-2xl border border-white/5 hover:border-white/10 transition-all group"
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-[10px] font-black border border-primary/20 text-primary-light overflow-hidden">
                  {review.userPhoto ? (
                    <img src={review.userPhoto} alt={review.userName} className="w-full h-full object-cover" />
                  ) : (
                    review.userName?.charAt(0) || '?'
                  )}
                </div>
                <div>
                  <h5 className="text-xs font-bold text-white group-hover:text-primary-light transition-colors">{review.userName}</h5>
                  <p className="text-[9px] text-white/80 uppercase font-medium">{review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString() : 'Just now'}</p>
                </div>
              </div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={`text-[10px] ${i < review.rating ? 'text-yellow-400' : 'text-white/10'}`}>★</span>
                ))}
              </div>
            </div>
            <p className="text-xs text-white/80 leading-relaxed font-light">{review.comment}</p>
          </motion.div>
        ))}
        {reviews.length === 0 && !showReviewForm && (
          <div className="text-center py-12 px-6 bg-white/5 rounded-3xl border border-dashed border-white/10">
            <MessageCircle className="mx-auto mb-3 text-white/10" size={32} />
            <p className="text-xs text-white/80 italic">{trackerT.noReviews}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section className="pt-32 pb-24 min-h-screen px-6">
      <SEO 
        title={t('seo.templatesTitle')}
        description={t('seo.templatesDesc')}
        keywords="website templates, web design projects, react templates, tailwind css examples, ecommerce templates"
        
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-end gap-6 mb-12">
          <div>
            <h2 className="text-4xl md:text-6xl font-display font-bold mb-4">{trackerT.title} <span className="text-primary-light">{trackerT.titleAccent}</span></h2>
            <p className="text-white/80">{trackerT.sub}</p>
          </div>
          
          <div className="flex flex-col sm:flex-row w-full xl:w-auto gap-4 items-start xl:items-end">
            <div className="relative w-full sm:w-64 md:w-80 group">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={trackerT.searchPlaceholder}
                className="w-full bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[1.25rem] px-6 py-4 pl-12 text-xs font-medium outline-none focus:border-primary-light/50 focus:bg-white/[0.05] transition-all hover:bg-white/5 text-white placeholder:text-white/80"
              />
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 group-focus-within:text-primary-light transition-colors" size={18} />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="relative w-full sm:w-auto">
              <select 
                className="appearance-none bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[1.25rem] w-full sm:w-min px-6 py-4 pr-12 text-[10px] sm:text-xs font-bold uppercase tracking-widest outline-none focus:border-primary-light/50 focus:bg-white/[0.05] transition-all cursor-pointer hover:bg-white/5 text-white/80"
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
              >
                {['all', 'web', 'mobile', 'ai', 'restaurant', 'ecommerce', 'business', 'portfolio', 'templates', 'branding', 'design'].map((cat) => (
                  <option key={cat} className="bg-[#0A0A0B] text-white py-2" value={cat}>{trackerT.filters[cat]}</option>
                ))}
              </select>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-primary-light">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </div>
            </div>
            
            <div className="relative w-full sm:w-auto">
              <select 
                className="appearance-none bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[1.25rem] w-full sm:w-min px-6 py-4 pr-12 text-[10px] sm:text-xs font-bold uppercase tracking-widest outline-none focus:border-primary-light/50 focus:bg-white/[0.05] transition-all cursor-pointer hover:bg-white/5 text-white/80"
                value={projectTypeFilter}
                onChange={(e) => setProjectTypeFilter(e.target.value as any)}
              >
                <option className="bg-[#0A0A0B] text-white py-2" value="all">All Types</option>
                <option className="bg-[#0A0A0B] text-white py-2" value="live">Live Websites</option>
                <option className="bg-[#0A0A0B] text-white py-2" value="demo">Demo Templates</option>
              </select>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-primary-light">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </div>
            </div>
            
            <div className="relative w-full sm:w-auto">
              <select 
                className="appearance-none bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-[1.25rem] w-full sm:w-min px-6 py-4 pr-12 text-[10px] sm:text-xs font-bold uppercase tracking-widest outline-none focus:border-primary-light/50 focus:bg-white/[0.05] transition-all cursor-pointer hover:bg-white/5 text-white/80"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option className="bg-[#0A0A0B] text-white py-2" value="popular">{trackerT.sort.popular}</option>
                <option className="bg-[#0A0A0B] text-white py-2" value="newest">{trackerT.sort.newest}</option>
                <option className="bg-[#0A0A0B] text-white py-2" value="seo">{trackerT.sort.seo}</option>
              </select>
              <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-primary-light">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {sortedProjects.map((project, index) => {
            const isNew = project.createdAt && typeof project.createdAt.toMillis === 'function' && project.createdAt.toMillis() > Date.now() - 7 * 24 * 60 * 60 * 1000;
            return (
              <motion.div 
                key={project.id} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (index % 3) * 0.1 }}
                onClick={() => {
                  setSelectedProject(project);
                  // Increment views counter immediately to state and firestore
                  try { updateDoc(doc(db, 'projects', project.id), { views: (project.views || 0) + 1 }) } catch (e) { console.error(e) }
                }}
                className="p-5 md:p-6 rounded-2xl border border-white/10 hover:border-primary-light/50 bg-charcoal/40 cursor-pointer transition-all relative group"
              >
                <div className="absolute -top-2 -left-2 w-8 h-8 bg-near-black border border-white/10 rounded-full flex items-center justify-center font-display font-bold text-xs text-primary-light z-10">
                  #{index + 1}
                </div>
                {isNew && (
                  <div className="absolute -top-3 -right-3 z-10 bg-primary text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-[0_0_25px_rgba(139,92,246,0.5)]">
                    NEW
                  </div>
                )}
                <div className="aspect-video rounded-lg overflow-hidden mb-4 relative">
                  <img src={project.thumbnail} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-near-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Eye className="text-white" />
                  </div>
                </div>
                <div className="flex justify-between items-start mb-2 gap-2">
                  <div className="min-w-0 flex-grow">
                    <h3 className="font-bold text-lg group-hover:text-primary-light transition-colors line-clamp-1">{project.title}</h3>
                    <span className={`inline-block mt-1 text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${(!project.projectType || project.projectType === 'live') ? 'bg-green-500/10 text-green-400' : 'bg-primary/20 text-primary-light'}`}>
                      {(!project.projectType || project.projectType === 'live') ? 'LIVE' : 'DEMO'}
                    </span>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleLike(project.id, project.likes); }}
                    className={`flex items-center gap-1 text-xs font-bold ${project.likes?.includes(auth.currentUser?.uid || '') ? 'text-primary-light' : 'text-white/80 hover:text-white'} shrink-0`}
                  >
                    <Heart size={14} fill={project.likes?.includes(auth.currentUser?.uid || '') ? 'currentColor' : 'none'} />
                    {project.likes?.length || 0}
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-white/80">
                  <span className="flex items-center gap-1 shrink-0"><Zap size={10} className="text-yellow-400" /> SEO: {project.seoScore || 0}%</span>
                  <span className="text-primary-light/60">#{project.category}</span>
                  <span className="hover:text-primary-light transition-colors cursor-default">#vizionidigjital</span>
                </div>
              </motion.div>
            );
          })}
          {sortedProjects.length === 0 && <div className="col-span-full text-center py-20 bg-white/5 rounded-3xl border border-dashed border-white/10"><p className="text-white/80 italic">{trackerT.noProjects}</p></div>}
        </div>
      </div>

      {/* Project Preview Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-8 bg-near-black/95 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-full max-w-7xl h-full md:h-[90vh] md:max-h-[90vh] glass-card md:rounded-3xl overflow-hidden flex flex-col relative"
            >
              {/* Close Button Mobile */}
              <button 
                aria-label="Close project modal"
                onClick={() => setSelectedProject(null)}
                className="md:hidden absolute top-4 right-4 z-[110] w-10 h-10 bg-near-black/80 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/20 active:scale-95 transition-all"
              >
                <span className="sr-only">Close modal</span>
                <X size={20} />
              </button>

              {/* Modal Header */}
              <div className="p-4 md:p-6 pb-2 md:pb-6 border-b border-white/10 bg-near-black/40 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pr-16 md:pr-6">
                <div className="flex items-center gap-3 w-full md:w-auto">
                  <div className="w-10 h-10 bg-primary/20 rounded-xl flex items-center justify-center border border-primary/20 shrink-0">
                    <Monitor size={20} className="text-primary-light" />
                  </div>
                  <div className="overflow-hidden">
                    <h3 className="text-lg md:text-xl font-bold text-white truncate max-w-[200px] md:max-w-md">{selectedProject.title}</h3>
                    <div className="flex items-center gap-2 text-[10px] text-white/80 uppercase tracking-widest font-bold">
                       <span className="text-primary-light">{selectedProject.category}</span>
                       <span>•</span>
                       <span className="flex items-center gap-1"><Eye size={12} /> {selectedProject.views || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto no-scrollbar pb-1 md:pb-0">
                  <a href={selectedProject.url} target="_blank" rel="noreferrer" className="btn-primary px-5 py-2.5 rounded-full font-bold transition-all flex items-center gap-2 text-sm whitespace-nowrap shadow-lg shadow-primary/20 shrink-0">
                    Live <ExternalLink size={14} />
                  </a>
                  
                  <div className="h-8 w-px bg-white/10 mx-2 hidden md:block" />

                  <div className="flex bg-white/5 rounded-full p-1 border border-white/10 shrink-0">
                    <button 
                      onClick={() => setDevicePreview('desktop')}
                      className={`p-2 rounded-full transition-all ${devicePreview === 'desktop' ? 'bg-near-black text-primary-light shadow-inner scale-110' : 'text-white/80 hover:text-white'}`}
                      title="Desktop Preview"
                    >
                      <Monitor size={16} />
                    </button>
                    <button 
                      onClick={() => setDevicePreview('tablet')}
                      className={`p-2 rounded-full transition-all ${devicePreview === 'tablet' ? 'bg-near-black text-primary-light shadow-inner scale-110' : 'text-white/80 hover:text-white'}`}
                      title="Tablet Preview"
                    >
                      <Tablet size={16} />
                    </button>
                    <button 
                      onClick={() => setDevicePreview('mobile')}
                      className={`p-2 rounded-full transition-all ${devicePreview === 'mobile' ? 'bg-near-black text-primary-light shadow-inner scale-110' : 'text-white/80 hover:text-white'}`}
                      title="Mobile Preview"
                    >
                      <Smartphone size={16} />
                    </button>
                  </div>

                  <button 
                    onClick={() => setIframeKey(Date.now())}
                    className="p-2.5 bg-white/5 hover:bg-white/10 rounded-full transition-all border border-white/10 text-white/80 hover:text-white shadow-sm shrink-0"
                    title="Refresh Preview"
                  >
                    <RotateCw size={16} />
                  </button>

                  <div className="h-8 w-px bg-white/10 mx-2 hidden md:block shrink-0" />

                  <button 
                    onClick={() => setSelectedProject(null)}
                    className="hidden md:flex p-2.5 bg-white/5 hover:bg-red-500/80 rounded-full transition-all border border-white/10 text-white/80 hover:text-white shadow-sm shrink-0"
                    title="Close"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="flex-grow flex flex-col md:flex-row overflow-y-auto md:overflow-hidden bg-black/20">
                {/* Main Content Area (Iframe) */}
                <div className="h-[60vh] md:h-auto w-full md:flex-1 md:min-w-0 relative flex flex-col items-center justify-center md:items-stretch md:justify-start p-2 md:p-8 md:overflow-y-auto no-scrollbar bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/[0.03] to-transparent">
                  {/* Iframe Wrapper */}
                  <div className="w-full flex-grow flex items-center justify-center relative md:min-h-[85vh]">
                    <div 
                      className={`
                        transition-all duration-700 ease-out shadow-[0_0_50px_rgba(0,0,0,0.5)] bg-white overflow-hidden relative
                        ${devicePreview === 'desktop' ? 'w-full h-full rounded-lg' : ''}
                        ${devicePreview === 'tablet' ? 'w-[768px] max-w-[95%] h-[1024px] max-h-[85vh] rounded-[2.5rem] border-[12px] border-[#1a1a1a] flex-shrink-0' : ''}
                        ${devicePreview === 'mobile' ? 'w-[375px] max-w-[95%] h-[812px] max-h-[85vh] rounded-[3.5rem] border-[14px] border-[#1a1a1a] flex-shrink-0' : ''}
                      `}
                    >
                      {/* Device Notch Simulation for mobile */}
                      {devicePreview === 'mobile' && (
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[40%] h-[30px] bg-[#1a1a1a] rounded-b-2xl z-20 flex items-center justify-center">
                          <div className="w-12 h-1 bg-white/10 rounded-full" />
                        </div>
                      )}
                      
                      {iframeLoading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-near-black z-10 animate-pulse">
                           <div className="flex flex-col items-center gap-4">
                              <div className="w-8 h-8 rounded-full border-2 border-primary-light border-t-transparent animate-spin" />
                              <p className="text-white/80 text-xs font-bold uppercase tracking-widest animate-bounce">Loading Preview...</p>
                           </div>
                        </div>
                      )}

                      <iframe 
                        key={iframeKey} 
                        src={selectedProject.url} 
                        onLoad={() => setIframeLoading(false)}
                        className={`w-full h-full border-none transition-opacity duration-500 ${iframeLoading ? 'opacity-0' : 'opacity-100'}`}
                        title={selectedProject.title} 
                      />
                    </div>
                  </div>
                  
                  {/* Reviews Section - Moved under the iframe on PC only */}
                  <div className="w-full max-w-4xl mx-auto mt-12 mb-8 bg-near-black/50 p-6 md:p-8 rounded-3xl border border-white/10 shrink-0 hidden md:block">
                    {renderReviews()}
                  </div>
                  {/* Warning Overlay for iframes that might be blocked */}
                  <div className="absolute top-6 right-6 z-20 pointer-events-none hidden md:block">
                     <div className="bg-near-black/80 backdrop-blur-md px-4 py-2 rounded-2xl text-[10px] text-white/80 border border-white/10 max-w-[240px] shadow-2xl">
                        <p className="flex items-center gap-2 mb-1 text-primary-light uppercase font-bold"><Zap size={12}/> {trackerT.livePreview?.title || 'Live Preview'}</p>
                        {trackerT.livePreview?.note || 'Dizajni i uebfaqes mund të ndryshojë brenda kornizës.'}
                     </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="w-full md:w-80 lg:w-96 bg-near-black/80 border-t md:border-t-0 md:border-l border-white/10 flex flex-col md:h-full shrink-0">
                  <div className="flex-grow overflow-visible md:overflow-y-auto custom-scrollbar">
                    {/* Tabs / Info Header */}
                    <div className="sticky top-0 z-10 bg-near-black/90 backdrop-blur-xl border-b border-white/5 p-6 space-y-4">
                      <div className="flex items-center justify-between">
                         <h4 className="font-bold text-white uppercase tracking-widest text-xs">{trackerT.description}</h4>
                         <div className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-tighter ${selectedProject.seoScore && selectedProject.seoScore > 80 ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                           SEO: {selectedProject.seoScore || 0}%
                         </div>
                      </div>
                      <p className="text-sm text-white/80 leading-relaxed italic line-clamp-4 hover:line-clamp-none transition-all cursor-default">
                        "{selectedProject.description}"
                      </p>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {['React', 'Tailwind', 'Responsive'].map(tag => (
                          <span key={tag} className="text-[10px] font-bold px-2 py-0.5 bg-white/5 rounded-md border border-white/5 text-white/80">#{tag}</span>
                        ))}
                      </div>
                    </div>

                    <div className="p-6 space-y-6">
                      <div className="bg-red-500/5 border border-red-500/20 p-4 rounded-2xl group transition-all hover:bg-red-500/10 mb-4">
                        <p className="text-[11px] text-red-200/60 leading-relaxed font-medium">
                          <strong className="text-red-400 flex items-center gap-2 mb-2"><X size={14}/> {trackerT.iframeWarning?.title || 'Problem me pamjen?'}</strong>
                          {trackerT.iframeWarning?.text || 'Nëse faqja nuk ngarkohet më lart, platformat e sigurisë mund të bllokojnë integrimin (iFrame).'} 
                          <a href={selectedProject.url} target="_blank" rel="noreferrer" className="text-white font-bold underline ml-1 hover:text-primary-light transition-colors">
                            {trackerT.iframeWarning?.link || 'Kliko këtu për ta parë direkt.'}
                          </a>
                        </p>
                      </div>
                      {/* Reviews for Mobile Sidebar */}
                      {renderReviews('block md:hidden')}
                    </div>
                  </div>
                  
                  {/* Modal Footer (Sticky) */}
                  <div className="p-6 border-t border-white/10 bg-near-black flex items-center justify-between gap-4 mt-auto sticky bottom-0 z-20">
                     <p className="text-[9px] text-white/80 uppercase tracking-tighter leading-tight max-w-[150px]">© 2026 Vizioni Digjital Templates System.</p>
                     <button 
                       onClick={() => { setSelectedProject(null); navigate('/contact'); }}
                       className="text-[10px] font-black uppercase tracking-widest text-primary-light px-4 py-2 rounded-full border border-primary/20 hover:bg-primary/10 transition-all flex items-center gap-2"
                     >
                       Interesuar? <ArrowRight size={12}/>
                     </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Auth Popup */}
      <AnimatePresence>
        {showAuthPopup && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-near-black/90 backdrop-blur-sm"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} 
              animate={{ scale: 1, y: 0 }} 
              exit={{ scale: 0.9, y: 20 }}
              className="bg-near-black border border-white/10 rounded-3xl p-8 max-w-sm w-full relative text-center shadow-2xl"
            >
              <button 
                aria-label="Close"
                onClick={() => setShowAuthPopup(false)}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                title="Close"
              >
                <X size={20} />
              </button>
              <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-primary/30">
                 <UserIcon size={32} className="text-primary-light" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Kyçu për të vazhduar</h3>
              <p className="text-sm text-white/80 mb-6">Për të lënë vlerësime dhe për të bashkëvepruar ju lutem kyçuni me llogarinë tuaj në Google.</p>
              
              <button 
                onClick={async () => {
                  try {
                    await signInWithGoogle();
                    setShowAuthPopup(false);
                  } catch(e) {
                    console.error(e);
                  }
                }}
                className="w-full bg-white text-black py-3 px-6 rounded-xl font-bold flex items-center justify-center gap-3 hover:bg-gray-200 transition-colors active:scale-95"
              >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                Vazhdo me Google
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

const PaymentPage = ({ plan, setIsChatOpen }: { plan: Plan | null, setIsChatOpen: (o: boolean) => void }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const paymentT = t('payment', { returnObjects: true }) as any;
  const [loading, setLoading] = useState(false);

  const handleStripeCheckout = async () => {
    if (!plan) return;
    setLoading(true);

    // Direct Stripe payment links provided by the user
    const paymentLinks: Record<string, string> = {
      launch: "https://buy.stripe.com/bJe9AU0ym1E83sfeYc6Zy00",
      growth: "https://buy.stripe.com/aFa5kE4OC1E85An03i6Zy01",
      enterprise: "https://buy.stripe.com/bJedRaa8W1E82ob6rG6Zy02",
      monthly: "https://buy.stripe.com/7sY14o1Cq82wgf1bM06Zy03",
      "project upgrade fee": "https://buy.stripe.com/7sY3cwgxk5Uo5AneYc6Zy04",
      upgrade: "https://buy.stripe.com/7sY3cwgxk5Uo5AneYc6Zy04"
    };

    const planKey = plan.name?.toLowerCase();
    if (paymentLinks[planKey]) {
      // Direct redirect to the provided payment link is extremely fast and robust
      window.location.href = paymentLinks[planKey];
      setLoading(false);
      return;
    }

    try {
      const resp = await fetch('/api/stripe/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planName: plan.name,
          price: plan.price,
          customerEmail: auth.currentUser?.email,
        })
      });
      const data = await resp.json();
      if (!resp.ok) {
        alert(data.error || "Failed to initialize checkout.");
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!plan) {
      navigate('/pricing');
    }
  }, [plan, navigate]);

  if (!plan) return null;

  const isSq = i18n?.language === 'sq';

  return (
    <section className="pt-32 pb-24 min-h-screen px-6 flex items-center justify-center">
      <SEO title="Payment" canonical="/payment" noindex={true} />
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-4xl w-full glass-card p-8 md:p-12 rounded-3xl relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1 bg-primary-light" />
        
        <button 
          onClick={() => navigate('/')}
          className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors"
        >
          <X size={24} />
        </button>

        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 bg-primary-light/10 rounded-xl flex items-center justify-center">
            <CreditCard className="text-primary-light" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">{paymentT.title}</h2>
            <p className="text-white/80 text-sm">Plan: <span className="text-primary-light font-bold">{plan?.name} ({plan?.price})</span></p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-8">
          {/* Credit Card / Stripe Option */}
          <div className="flex-1 bg-white/5 rounded-2xl p-6 border border-white/10 flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <CreditCard size={20} className="text-primary-light" /> {paymentT.stripeTitle}
              </h3>
              <p className="text-white/80 mb-8 whitespace-pre-line text-sm leading-relaxed">
                {paymentT.stripeDesc}
              </p>
            </div>
            
            <div>
              <button 
                onClick={handleStripeCheckout}
                disabled={loading}
                className="w-full bg-[#635BFF] text-white py-4 rounded-xl font-bold text-center hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50 mb-4"
              >
                {loading ? paymentT.connecting : paymentT.payWithCard} <ExternalLink size={18} />
              </button>

              <div className="pt-4 border-t border-white/5 text-center">
                <p className="text-xs text-white/80 mb-2">
                  {isSq 
                    ? "Dëshironi të abonoheni në mirëmbajtjen mujore (49€/muaj)?"
                    : "Want to subscribe to optional monthly maintenance (49€/month)?"}
                </p>
                <a 
                  href="https://buy.stripe.com/7sY14o1Cq82wgf1bM06Zy03"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary-light hover:underline font-semibold inline-flex items-center gap-1"
                >
                  {isSq ? "Kliko këtu për t'u pajtuar" : "Click here to subscribe"} <ExternalLink size={12} />
                </a>
              </div>
            </div>
          </div>

          {/* OneFor Option */}
          <div className="flex-1 bg-white/5 rounded-2xl p-6 border border-white/10 flex flex-col items-center text-center">
             <div className="w-full flex items-center justify-center gap-2 mb-4">
                <Smartphone size={20} className="text-[#E80B44]" />
                <h3 className="text-xl font-bold">{paymentT.oneForTitle}</h3>
             </div>
             
             <img 
               src="/qrcodeonefor.png" 
               alt="OneFor QR Code" 
               className="w-40 h-40 object-contain rounded-xl bg-white p-2 mb-4"
             />

             <div className="flex items-center gap-2 mb-4 bg-white/10 px-4 py-2 rounded-lg border border-white/20">
               <span className="text-white/80 text-sm">Username:</span>
               <span className="text-primary-light font-bold tracking-wide">egzonpeci1</span>
               <button 
                 onClick={() => navigator.clipboard.writeText('egzonpeci1')}
                 className="ml-2 hover:bg-white/10 p-1 rounded transition-colors text-white/80 hover:text-white"
                 title="Copy username"
               >
                 <Copy size={14} />
               </button>
             </div>
             
             <p className="text-sm text-white/80 mb-6 px-4">
               {paymentT.instruction}
             </p>

             <div className="flex flex-col sm:flex-row gap-4 w-full">
               <button 
                 onClick={() => {
                   // Attempt to open OneFor app through a custom URL scheme
                   window.location.href = 'onefor://';
                   // Fallback to website if the app is not installed
                   setTimeout(() => {
                     window.open('https://web.app.onefor.com/web/contact?userName=egzonpeci1', '_blank');
                   }, 1500);
                 }}
                 className="flex-1 bg-[#E80B44] text-white py-3 rounded-xl font-bold text-center hover:opacity-90 transition-all flex items-center justify-center gap-2 text-sm"
               >
                 <Smartphone size={16} /> {paymentT.cta1}
               </button>
               <button 
                 onClick={() => {
                   navigate('/');
                   setIsChatOpen(true);
                 }}
                 className="flex-1 border border-white/10 hover:bg-white/5 py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-sm"
               >
                 <MessageCircle size={16} /> {paymentT.cta2}
               </button>
             </div>
          </div>
        </div>
        
        <div className="mt-8 flex flex-col items-center gap-4 text-center">
          <div className="flex flex-col items-center gap-2">
            <p className="text-[10px] text-white/80 uppercase tracking-widest font-bold">
              {paymentT.footer}
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-light/10 border border-primary-light/20 text-primary-light text-[10px] font-bold">
              <Mail size={12} /> vizionidigjital@gmail.com
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 pt-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/20 text-green-500 text-[10px] font-bold uppercase tracking-wider">
              <Shield size={12} /> Verified Stripe Secured Payments
            </div>
            <p className="text-[10px] text-white/80 italic">
              {paymentT.disclaimer}
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

const SuccessPage = () => {
  const navigate = useNavigate();
  return (
    <section className="pt-32 pb-24 min-h-screen px-6 flex items-center justify-center">
      <SEO title="Payment Successful" noindex={true} />
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-xl w-full glass-card p-12 rounded-3xl text-center"
      >
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
          <CheckCircle2 className="text-green-500 w-10 h-10" />
        </div>
        <h1 className="text-3xl font-display font-bold mb-4">Pagesa u krye me sukses!</h1>
        <p className="text-white/80 mb-10">
          Faleminderit që zgjodhët Vizioni Digjital. Pagesa juaj është procesuar me sukses dhe plani juaj do të përditësohet së shpejti.
        </p>
        <button 
          onClick={() => navigate('/')}
          className="btn-primary px-8 py-3 rounded-full font-bold hover:shadow-[0_0_25px_rgba(139,92,246,0.4)] transition-all"
        >
          Kthehu në Ballinë
        </button>
      </motion.div>
    </section>
  );
};

const CancelPage = () => {
  const navigate = useNavigate();
  return (
    <section className="pt-32 pb-24 min-h-screen px-6 flex items-center justify-center">
      <SEO title="Payment Cancelled" noindex={true} />
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-xl w-full glass-card p-12 rounded-3xl text-center"
      >
        <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-8">
          <X className="text-red-500 w-10 h-10" />
        </div>
        <h1 className="text-3xl font-display font-bold mb-4">Pagesa u anulua</h1>
        <p className="text-white/80 mb-10">
          Procesi i pagesës u ndërpre. Nëse keni ndonjë problem, ju lutemi na kontaktoni.
        </p>
        <button 
          onClick={() => navigate('/')}
          className="bg-white/10 hover:bg-white/20 px-8 py-3 rounded-full font-bold transition-all"
        >
          Kthehu në Ballinë
        </button>
      </motion.div>
    </section>
  );
};

import { BlogGeneratorTab } from './components/BlogGeneratorTab';
import { GlobalSEOTab } from './components/GlobalSEO';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [activeTab, setActiveTab] = useState<'analytics' | 'messages' | 'projects' | 'users' | 'subscribers' | 'blog' | 'seo'>('analytics');
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [newProject, setNewProject] = useState({ title: '', url: '', description: '', category: 'web' as const, projectType: 'live' as 'live' | 'demo' });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [viewingImage, setViewingImage] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const adminT = t('admin', { returnObjects: true }) as any;

  useEffect(() => {
    onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  useEffect(() => {
    if (user?.email === 'casperxoni@gmail.com') {
      const q = query(collection(db, 'messages'), orderBy('createdAt', 'desc'));
      const unsubscribeMessages = onSnapshot(q, (snapshot) => {
        setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Message)));
      });
      const unsubscribeProjects = onSnapshot(collection(db, 'projects'), (snapshot) => {
        setProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project)));
      });
      const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
        setUsers(snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile)));
      });
      const unsubscribeSubscribers = onSnapshot(collection(db, 'subscribers'), (snapshot) => {
        setSubscribers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      });
      return () => { 
        unsubscribeMessages(); 
        unsubscribeProjects(); 
        unsubscribeUsers();
        unsubscribeSubscribers();
      };
    }
  }, [user]);

  const handleUpdateUserSubscription = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await updateDoc(doc(db, 'users', editingUser.uid), {
        plan: editingUser.plan,
        planExpiresAt: editingUser.planExpiresAt,
        status: editingUser.status
      });
      setEditingUser(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `users/${editingUser.uid}`);
    }
  };

  const handleMarkAsRead = async (id: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'messages', id), { read: !currentStatus });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `messages/${id}`);
    }
  };

  const handleAddProject = async (e: FormEvent) => {
    e.preventDefault();
    const thumbnail = `https://s.wordpress.com/mshots/v1/${encodeURIComponent(newProject.url)}?w=600&h=450`;
    const seoScore = calculateSeoScore({ ...newProject, thumbnail });
    try {
      if (editingProjectId) {
        await updateDoc(doc(db, 'projects', editingProjectId), {
          title: newProject.title,
          url: newProject.url,
          description: newProject.description,
          category: newProject.category,
          projectType: newProject.projectType,
          thumbnail,
          seoScore
        });
        setEditingProjectId(null);
      } else {
        const docRef = await addDoc(collection(db, 'projects'), { 
          ...newProject, 
          thumbnail,
          seoScore,
          createdAt: serverTimestamp() 
        });

        // Send Newsletter
        try {
          const usersSnap = await getDocs(collection(db, 'users'));
          const emails = usersSnap.docs.map(doc => doc.data().email).filter(email => email);
          if (emails.length > 0) {
            fetch('/api/email/newsletter', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                emails: emails,
                projectTitle: newProject.title,
                projectLink: `https://vizionidigjital.com/templates?id=${docRef.id}`
              })
            }).catch(console.error);
          }
        } catch (err) {
          console.error("Failed fetching users for newsletter:", err);
        }
      }

      setNewProject({ title: '', url: '', description: '', category: 'web' });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'projects');
    }
  };

  const handleSendWelcome = async () => {
    if (!editingUser) return;
    try {
      await fetch('/api/email/welcome', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: editingUser.email, displayName: editingUser.displayName })
      });
      alert('Welcome email sent!');
    } catch (e) {
      alert('Failed to send email');
    }
  };

  const handleSendExpiration = async () => {
    if (!editingUser) return;
    try {
      const daysLeft = editingUser.planExpiresAt ? Math.max(0, Math.ceil((editingUser.planExpiresAt.toMillis() - Date.now()) / (1000 * 60 * 60 * 24))) : 0;
      await fetch('/api/email/expiration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
           email: editingUser.email, 
           displayName: editingUser.displayName, 
           planName: editingUser.plan,
           daysLeft: daysLeft || 3
        })
      });
      alert('Expiration email sent!');
    } catch (e) {
      alert('Failed to send email');
    }
  };

  const [confirmDelete, setConfirmDelete] = useState<{id: string, type: 'project' | 'message'} | null>(null);

  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteDoc(doc(db, 'projects', projectId));
      setConfirmDelete(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `projects/${projectId}`);
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    try {
      await deleteDoc(doc(db, 'messages', messageId));
      setConfirmDelete(null);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `messages/${messageId}`);
    }
  };

  if (!user || user.email !== 'casperxoni@gmail.com') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <SEO title="Admin Access" canonical="/admin" noindex={true} />
        <div className="glass-card p-12 rounded-3xl text-center max-w-md w-full">
          <Lock size={48} className="text-primary-light mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">{adminT.accessDenied}</h2>
          <p className="text-white/80 mb-8">{adminT.accessDeniedSub}</p>
          <button onClick={signInWithGoogle} className="w-full btn-primary py-4 rounded-xl font-bold flex items-center justify-center gap-2">
            Login with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-6 min-h-screen">
      <SEO title={adminT.title} canonical="/admin" noindex={true} />
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-4xl font-display font-bold">{adminT.title} <span className="text-primary-light">{adminT.titleAccent}</span></h2>
          <button onClick={() => signOut(auth)} className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
            <LogOut size={18} /> {adminT.logout}
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="glass-card p-6 rounded-2xl border border-white/5">
            <p className="text-[10px] text-white/80 uppercase tracking-widest font-bold mb-1">{adminT.stats.totalMessages}</p>
            <p className="text-3xl font-bold">{messages.length}</p>
          </div>
          <div className="glass-card p-6 rounded-2xl border border-white/5">
            <p className="text-[10px] text-white/80 uppercase tracking-widest font-bold mb-1">{adminT.stats.unread}</p>
            <p className="text-3xl font-bold text-primary-light">{messages.filter(m => !m.read).length}</p>
          </div>
          <div className="glass-card p-6 rounded-2xl border border-white/5">
            <p className="text-[10px] text-white/80 uppercase tracking-widest font-bold mb-1">{adminT.stats.liveProjects}</p>
            <p className="text-3xl font-bold">{projects.length}</p>
          </div>
          <div className="glass-card p-6 rounded-2xl border border-white/5">
            <p className="text-[10px] text-white/80 uppercase tracking-widest font-bold mb-1">{adminT.stats.paymentConfirmations}</p>
            <p className="text-3xl font-bold text-green-400">{messages.filter(m => m.screenshot).length}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2 scrollbar-hide">
          <button onClick={() => setActiveTab('analytics')} className={`px-4 md:px-6 py-2 rounded-full font-bold transition-all text-sm md:text-base whitespace-nowrap ${activeTab === 'analytics' ? 'bg-primary text-white' : 'bg-white/5'}`}>Analytics</button>
          <button onClick={() => setActiveTab('messages')} className={`px-4 md:px-6 py-2 rounded-full font-bold transition-all text-sm md:text-base whitespace-nowrap ${activeTab === 'messages' ? 'bg-primary text-white' : 'bg-white/5'}`}>{adminT.tabs?.messages || 'Messages'} ({messages.length})</button>
          <button onClick={() => setActiveTab('projects')} className={`px-4 md:px-6 py-2 rounded-full font-bold transition-all text-sm md:text-base whitespace-nowrap ${activeTab === 'projects' ? 'bg-primary text-white' : 'bg-white/5'}`}>{adminT.tabs?.projects || 'Projects'} ({projects.length})</button>
          <button onClick={() => setActiveTab('users')} className={`px-4 md:px-6 py-2 rounded-full font-bold transition-all text-sm md:text-base whitespace-nowrap ${activeTab === 'users' ? 'bg-primary text-white' : 'bg-white/5'}`}>{adminT.tabs?.users || 'Users'} ({users.length})</button>
          <button onClick={() => setActiveTab('subscribers')} className={`px-4 md:px-6 py-2 rounded-full font-bold transition-all text-sm md:text-base whitespace-nowrap ${activeTab === 'subscribers' ? 'bg-primary text-white' : 'bg-white/5'}`}>Subscribers ({subscribers.length})</button>
          <button onClick={() => setActiveTab('blog')} className={`px-4 md:px-6 py-2 rounded-full font-bold transition-all text-sm md:text-base whitespace-nowrap ${activeTab === 'blog' ? 'bg-primary text-white' : 'bg-white/5'}`}>Blog Generator</button>
          <button onClick={() => setActiveTab('seo')} className={`px-4 md:px-6 py-2 rounded-full font-bold transition-all text-sm md:text-base whitespace-nowrap ${activeTab === 'seo' ? 'bg-primary text-white' : 'bg-white/5'}`}>Global SEO</button>
        </div>

        {activeTab === 'messages' ? (
          <div className="grid gap-6">
            {messages.map(msg => (
              <div key={msg.id} className={`glass-card p-6 rounded-2xl border transition-all ${msg.read ? 'border-white/5 opacity-60' : 'border-primary-light/30 shadow-[0_0_25px_rgba(139,92,246,0.05)]'}`}>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${msg.read ? 'bg-white/10' : 'bg-primary-light shadow-[0_0_15px_rgba(139,92,246,0.6)]'}`} />
                    <div>
                      <h3 className="font-bold text-base md:text-lg">{msg.name} {msg.surname || ''} {msg.username ? `(@${msg.username})` : ''}</h3>
                      <p className="text-primary-light text-xs md:text-sm">{msg.email}</p>
                    </div>
                  </div>
                  <span className="text-[10px] md:text-xs text-white/80">{msg.createdAt?.toDate?.() ? msg.createdAt.toDate().toLocaleString() : '...'}</span>
                </div>
                <p className="text-white/80 text-sm md:text-base mb-6 bg-white/5 p-4 rounded-lg whitespace-pre-wrap">{msg.text || (msg as any).message}</p>
                {msg.screenshot && (
                  <div className="mb-6">
                    <p className="text-[10px] text-white/80 uppercase tracking-widest font-bold mb-2">{adminT.messages.screenshotConfirmation}</p>
                    <div className="relative group cursor-pointer w-fit" onClick={() => setViewingImage(msg.screenshot || null)}>
                      <img src={msg.screenshot} className="max-w-xs rounded-lg border border-white/10 hover:border-primary-light transition-colors" alt="Confirmation" loading="lazy" />
                      <div className="absolute inset-0 bg-near-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity rounded-lg">
                        <Eye className="text-white" />
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <a 
                    href={`mailto:${msg.email}?subject=${encodeURIComponent(adminT.messages.replySubject)}&body=${encodeURIComponent(adminT.messages.replyBody.replace('{{name}}', msg.name))}%0D%0A%0D%0A`}
                    className="bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                  >
                    <Send size={14} /> {adminT.messages.replyEmail}
                  </a>
                  <button 
                    onClick={() => {
                      const text = adminT.messages.paymentText.replace('{{name}}', msg.name);
                      window.open(`mailto:${msg.email}?subject=${encodeURIComponent(adminT.messages.paymentSubject)}&body=${encodeURIComponent(text)}`);
                    }}
                    className="bg-green-500/10 hover:bg-green-500/20 text-green-400 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                  >
                    <CheckCircle2 size={14} /> {adminT.messages.confirmPayment}
                  </button>
                  <button 
                    onClick={() => handleMarkAsRead(msg.id, !!msg.read)}
                    className={`px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 transition-all ${msg.read ? 'bg-white/5 text-white/80' : 'bg-primary-light/10 text-primary-light hover:bg-primary-light/20'}`}
                  >
                    {msg.read ? adminT.messages.markAsUnread : adminT.messages.markAsRead}
                  </button>
                  {confirmDelete?.id === msg.id ? (
                    <div className="flex gap-2">
                      <button 
                        onClick={() => handleDeleteMessage(msg.id)}
                        className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-bold"
                      >
                        {adminT.messages.confirmDelete}
                      </button>
                      <button 
                        onClick={() => setConfirmDelete(null)}
                        className="bg-white/10 px-4 py-2 rounded-lg text-sm font-bold"
                      >
                        {adminT.messages.cancel}
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={() => setConfirmDelete({id: msg.id, type: 'message'})}
                      className="bg-red-500/10 hover:bg-red-500/20 text-red-500 px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                    >
                      <Trash2 size={14} /> {adminT.messages.delete}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : activeTab === 'projects' ? (
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="glass-card p-8 rounded-3xl h-fit">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2"><Plus size={20} className="text-primary-light" /> {editingProjectId ? 'Përditëso Projektin' : adminT.addProject.title}</h3>
              <div className="mb-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-white/60">Auto Plotësim (Kërkon URL e uebfaqes)</label>
                  <button 
                    type="button" 
                    onClick={async () => {
                      if (!newProject.url) {
                        alert("Ju lutem shënoni URL e uebfaqes së pari.");
                        return;
                      }
                      setIsAnalyzing(true);
                      try {
                        const res = await fetch("/api/ai/analyze-website", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({ url: newProject.url })
                        });
                        if (!res.ok) {
                          let errorMsg = "Dështoi analiza";
                          try {
                            const errorText = await res.text();
                            const errorData = JSON.parse(errorText);
                            errorMsg = errorData.error || errorMsg;
                          } catch (e) {
                            errorMsg = "Server returned an invalid response. Please try again.";
                          }
                          throw new Error(errorMsg);
                        }
                        const textData = await res.text();
                        let data;
                        try {
                          data = JSON.parse(textData);
                        } catch (e) {
                          throw new Error("Invalid JSON from AI Analysis.");
                        }
                        setNewProject(prev => ({
                          ...prev,
                          title: data.title || prev.title,
                          description: data.description || prev.description,
                          category: (['web', 'mobile', 'ai', 'restaurant', 'ecommerce', 'business', 'portfolio', 'templates'].includes(data.category?.toLowerCase()) ? data.category.toLowerCase() : prev.category) as any
                        }));
                      } catch (error: any) {
                        console.error(error);
                        alert("Gabim gjatë analizës nga AI: " + error.message);
                      } finally {
                        setIsAnalyzing(false);
                      }
                    }}
                    disabled={isAnalyzing || !newProject.url}
                    className="btn-primary py-1 px-3 rounded-lg text-xs font-bold flex items-center gap-2 disabled:opacity-50"
                  >
                    <Zap size={14} className={isAnalyzing ? "animate-pulse" : ""} />
                    {isAnalyzing ? "Duke analizuar..." : "Auto AI Info"}
                  </button>
                </div>
              </div>
              <form onSubmit={handleAddProject} className="space-y-4">
                <input required placeholder="URL e Uebfaqes" className="w-full bg-white/5 border border-white/10 rounded-lg p-3 outline-none focus:border-primary-light" value={newProject.url} onChange={e => setNewProject({...newProject, url: e.target.value})} />
                <input required placeholder="Titulli" className="w-full bg-white/5 border border-white/10 rounded-lg p-3 outline-none focus:border-primary-light" value={newProject.title} onChange={e => setNewProject({...newProject, title: e.target.value})} />
                <select 
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 outline-none focus:border-primary-light text-white/80"
                  value={newProject.category}
                  onChange={e => setNewProject({...newProject, category: e.target.value as any})}
                >
                  <option value="web">Web Design</option>
                  <option value="mobile">Mobile App</option>
                  <option value="ai">AI Solution</option>
                  <option value="restaurant">Restaurant/Cafe</option>
                  <option value="ecommerce">E-Commerce</option>
                  <option value="business">Business</option>
                  <option value="portfolio">Portfolio</option>
                  <option value="templates">Templates</option>
                  <option value="branding">Branding</option>
                  <option value="design">Design</option>
                </select>
                <select 
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 outline-none focus:border-primary-light text-white/80"
                  value={newProject.projectType}
                  onChange={e => setNewProject({...newProject, projectType: e.target.value as any})}
                >
                  <option value="live">Live Website</option>
                  <option value="demo">Demo Template</option>
                </select>
                <textarea placeholder="Përshkrimi" className="w-full bg-white/5 border border-white/10 rounded-lg p-3 outline-none focus:border-primary-light resize-none" rows={3} value={newProject.description} onChange={e => setNewProject({...newProject, description: e.target.value})} />
                <button className="w-full btn-primary py-4 rounded-xl font-bold">{editingProjectId ? 'Përditëso' : adminT.addProject.submit}</button>
                {editingProjectId && (
                  <button type="button" onClick={() => { setEditingProjectId(null); setNewProject({ title: '', url: '', description: '', category: 'web', projectType: 'live' }); }} className="w-full bg-white/10 text-white py-4 rounded-xl font-bold mt-2">Anulo</button>
                )}
                <p className="text-[10px] text-white/80 italic mt-2">{adminT.addProject.thumbnailNote}</p>
              </form>
            </div>
            <div className="space-y-4">
              {projects.map(p => {
                const isNew = p.createdAt && typeof p.createdAt.toMillis === 'function' && p.createdAt.toMillis() > Date.now() - 7 * 24 * 60 * 60 * 1000;
                return (
                <div key={p.id} className="glass-card p-4 rounded-2xl flex flex-col sm:flex-row gap-4 items-center justify-between group relative">
                  {isNew && (
                    <div className="absolute -top-1 -left-1 z-10 bg-primary text-white text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shadow-[0_0_15px_rgba(139,92,246,0.3)]">
                      NEW
                    </div>
                  )}
                  <div className={`absolute -top-1 -right-1 z-10 text-[8px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shadow-[0_0_15px_rgba(0,0,0,0.3)] ${(!p.projectType || p.projectType === 'live') ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
                    {(!p.projectType || p.projectType === 'live') ? 'LIVE' : 'DEMO'}
                  </div>
                  <div className="flex gap-4 items-center w-full">
                    <img src={p.thumbnail} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover bg-white/5 flex-shrink-0" alt="" loading="lazy" />
                    <div className="min-w-0 flex-grow">
                      <h4 className="font-bold text-sm sm:text-base truncate">{p.title}</h4>
                      <div className="flex items-center gap-2 overflow-hidden">
                        <p className="text-[10px] sm:text-xs text-white/80 truncate flex-grow">{p.url}</p>
                        <a href={p.url} target="_blank" rel="noreferrer" className="text-[9px] bg-primary-light/10 text-primary-light px-2 py-0.5 rounded-full hover:bg-primary hover:text-white transition-colors font-bold uppercase tracking-widest flex-shrink-0">Live</a>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto justify-end sm:opacity-0 group-hover:opacity-100 transition-opacity border-t sm:border-none border-white/5 pt-3 sm:pt-0">
                    {confirmDelete?.id === p.id ? (
                      <div className="flex gap-2 w-full justify-end">
                        <button 
                          onClick={() => handleDeleteProject(p.id)}
                          className="bg-red-500 text-white px-3 py-1 rounded-lg text-xs font-bold"
                        >
                          {adminT.messages.delete}
                        </button>
                        <button 
                          onClick={() => setConfirmDelete(null)}
                          className="bg-white/10 px-3 py-1 rounded-lg text-xs font-bold"
                        >
                          X
                        </button>
                      </div>
                    ) : (
                      <>
                        <button 
                          onClick={() => {
                            setEditingProjectId(p.id);
                            setNewProject({ title: p.title, url: p.url, description: p.description || '', category: p.category as any || 'web', projectType: p.projectType || 'live' });
                          }}
                          className="p-2 sm:p-3 text-white/80 hover:text-primary-light hover:bg-primary-light/10 rounded-xl transition-all"
                          title="Edit Project"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/></svg>
                        </button>
                        <button 
                          onClick={() => setConfirmDelete({id: p.id, type: 'project'})}
                          className="p-2 sm:p-3 text-white/80 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all"
                          title="Delete Project"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )})}
            </div>
          </div>
        ) : activeTab === 'users' ? (
          <div className="grid gap-6">
            {users.map(u => {
              const isExpired = u.planExpiresAt && u.planExpiresAt.toMillis() < Date.now();
              const status = isExpired ? 'inactive' : (u.status || 'active');
              return (
                <div key={u.uid} className="glass-card p-6 rounded-2xl border border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="flex items-center gap-4">
                    <img src={u.photoURL} className="w-12 h-12 rounded-full border border-white/10" alt="" loading="lazy" />
                    <div>
                      <h3 className="font-bold">{u.displayName}</h3>
                      <p className="text-xs text-white/80">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-4 items-center">
                    <div className="text-center">
                      <p className="text-[10px] text-white/80 uppercase font-bold">{adminT.users.plan}</p>
                      <p className="text-sm font-bold capitalize text-primary-light">{u.plan}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-white/80 uppercase font-bold">{adminT.users.expiry}</p>
                      <p className="text-sm font-bold">{u.planExpiresAt?.toDate?.() ? u.planExpiresAt.toDate().toLocaleDateString() : 'N/A'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-white/80 uppercase font-bold">{adminT.users.status}</p>
                      <span className={`text-[10px] px-2 py-1 rounded-full font-bold uppercase ${status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                        {status === 'active' ? adminT.users.active : adminT.users.inactive}
                      </span>
                    </div>
                    <button 
                      onClick={() => setEditingUser(u)}
                      className="bg-white/5 hover:bg-white/10 p-2 rounded-lg transition-all"
                    >
                      <LayoutDashboard size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : activeTab === 'subscribers' ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {subscribers.map(s => (
               <div key={s.id} className="glass-card p-6 rounded-2xl border border-white/5 flex items-center justify-between">
                 <div>
                   <p className="font-bold text-lg">{s.email}</p>
                   <p className="text-xs text-white/80 uppercase tracking-widest mt-1">Status: <span className="text-green-400">{s.status || 'active'}</span></p>
                 </div>
               </div>
            ))}
            {subscribers.length === 0 && <p className="text-white/50 col-span-full">No subscribers found.</p>}
          </div>
        ) : activeTab === 'blog' ? (
          <BlogGeneratorTab />
        ) : activeTab === 'seo' ? (
          <GlobalSEOTab />
        ) : (
          <div className="glass-card p-8 md:p-12 rounded-3xl border border-white/5 space-y-12">
            <h3 className="text-3xl font-bold font-display">Advanced Analytics</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white/5 p-6 rounded-2xl border border-white/5 text-center">
                 <h4 className="font-bold text-white/60 uppercase tracking-widest text-[10px] mb-3">Newsletter Subscribers</h4>
                 <p className="text-5xl font-display font-extrabold text-primary-light">{subscribers.length}</p>
              </div>
              <div className="bg-white/5 p-6 rounded-2xl border border-white/5 text-center">
                 <h4 className="font-bold text-white/60 uppercase tracking-widest text-[10px] mb-3">Registered Users</h4>
                 <p className="text-5xl font-display font-extrabold text-primary-light">{users.length}</p>
              </div>
              <div className="bg-white/5 p-6 rounded-2xl border border-white/5 text-center">
                 <h4 className="font-bold text-white/60 uppercase tracking-widest text-[10px] mb-3">Projects Managed</h4>
                 <p className="text-5xl font-display font-extrabold text-primary-light">{projects.length}</p>
              </div>
              <div className="bg-white/5 p-6 rounded-2xl border border-white/5 text-center">
                 <h4 className="font-bold text-white/60 uppercase tracking-widest text-[10px] mb-3">Client Messages</h4>
                 <p className="text-5xl font-display font-extrabold text-primary-light">{messages.length}</p>
              </div>
            </div>
          </div>
        )}


        <AnimatePresence>
          {editingUser && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-near-black/95 backdrop-blur-md"
            >
              <motion.div 
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="w-full max-w-md bg-near-black border border-white/10 p-6 md:p-8 rounded-[2.5rem] relative shadow-2xl overflow-y-auto max-h-[90vh] scrollbar-hide"
              >
                <button onClick={() => setEditingUser(null)} className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors bg-white/5 p-2 rounded-full"><X size={20} /></button>
                <h3 className="text-xl md:text-2xl font-bold mb-6 font-display">{adminT.users.manageSub || 'Manage Subscription'}</h3>
                
                <div className="flex items-center gap-4 mb-8 bg-white/5 p-4 rounded-3xl border border-white/5">
                  <img src={editingUser.photoURL} className="w-12 h-12 rounded-full border border-white/10" alt="" />
                  <div className="min-w-0">
                    <h4 className="font-bold truncate">{editingUser.displayName}</h4>
                    <p className="text-xs text-white/80 truncate">{editingUser.email}</p>
                  </div>
                </div>

                <form onSubmit={handleUpdateUserSubscription} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] text-white/80 uppercase font-bold tracking-widest ml-1">{adminT.users.plan}</label>
                    <select 
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 outline-none focus:border-primary-light transition-colors text-white appearance-none"
                      value={editingUser.plan}
                      onChange={e => setEditingUser({...editingUser, plan: e.target.value as any})}
                    >
                      <option value="Standard">Standard</option>
                      <option value="Premium">Premium</option>
                      <option value="Enterprise">Enterprise</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-[10px] text-white/80 uppercase font-bold tracking-widest ml-1">{adminT.users.expiry}</label>
                    <input 
                      type="date"
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-4 outline-none focus:border-primary-light transition-colors text-white"
                      value={editingUser.planExpiresAt?.toDate?.() ? editingUser.planExpiresAt.toDate().toISOString().split('T')[0] : ''}
                      onChange={e => {
                        const date = new Date(e.target.value);
                        setEditingUser({...editingUser, planExpiresAt: Timestamp.fromDate(date)});
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] text-white/80 uppercase font-bold tracking-widest ml-1">{adminT.users.status}</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button 
                        type="button"
                        onClick={() => setEditingUser({...editingUser, status: 'active'})}
                        className={`py-3 rounded-2xl font-bold border transition-all ${editingUser.status === 'active' ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-white/5 border-transparent text-white/80 hover:text-white'}`}
                      >
                        Active
                      </button>
                      <button 
                        type="button"
                        onClick={() => setEditingUser({...editingUser, status: 'inactive'})}
                        className={`py-3 rounded-2xl font-bold border transition-all ${editingUser.status === 'inactive' ? 'bg-red-500/20 border-red-500/50 text-red-400' : 'bg-white/5 border-transparent text-white/80 hover:text-white'}`}
                      >
                        Inactive
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <button type="button" onClick={handleSendWelcome} className="bg-white/5 hover:bg-white/10 text-white text-[10px] uppercase tracking-widest py-3 rounded-2xl font-bold transition-colors">
                      Welcome Email
                    </button>
                    <button type="button" onClick={handleSendExpiration} className="bg-white/5 hover:bg-white/10 text-white text-[10px] uppercase tracking-widest py-3 rounded-2xl font-bold transition-colors">
                      Expiry Warning
                    </button>
                  </div>

                  <button type="submit" className="w-full btn-primary py-5 rounded-2xl font-bold shadow-[0_0_30px_rgba(139,92,246,0.3)] hover:shadow-[0_0_40px_rgba(139,92,246,0.4)] transition-all">
                    {adminT.users.save}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {viewingImage && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-near-black/95 flex items-center justify-center p-6 md:p-12"
            onClick={() => setViewingImage(null)}
          >
            <button className="absolute top-8 right-8 text-white/80 hover:text-white transition-colors">
              <X size={32} />
            </button>
            <motion.img 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              src={viewingImage} 
              className="max-w-full max-h-full rounded-xl shadow-2xl border border-white/10"
              alt="Full Size"
              onClick={e => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SubscriptionNotification = ({ profile }: { profile: UserProfile | null }) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [msg, setMsg] = useState('');
  const subT = t('subscription', { returnObjects: true }) as any;

  useEffect(() => {
    if (profile?.planExpiresAt) {
      const expiresAt = profile.planExpiresAt.toMillis();
      const now = Date.now();
      const diffDays = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));

      if (diffDays <= 7 && diffDays > 0) {
        setShow(true);
        if (diffDays === 1) setMsg(subT.expiresTomorrow);
        else setMsg(subT.expiresInDays.replace('{{days}}', diffDays.toString()));
      } else if (diffDays <= 0) {
        setShow(true);
        setMsg(subT.expired);
      }
    }
  }, [profile, subT]);

  if (!show) return null;

  return (
    <motion.div 
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed bottom-8 right-8 z-[200] max-w-sm w-full"
    >
      <div className="glass-card p-6 rounded-2xl border border-primary-light/50 shadow-[0_0_25px_rgba(139,92,246,0.2)] relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-primary-light" />
        <button aria-label="Close notification" onClick={() => setShow(false)} className="absolute top-4 right-4 text-white/80 hover:text-white"><X size={16} /></button>
        <div className="flex items-center gap-4 mb-3">
          <div className="w-10 h-10 bg-primary-light/10 rounded-full flex items-center justify-center">
            <Zap className="text-primary-light" size={20} />
          </div>
          <h4 className="font-bold text-primary-light">{subT.notification}</h4>
        </div>
        <p className="text-sm text-white/80 leading-relaxed mb-4">{msg}</p>
        <div className="flex gap-2">
          <a 
            href={`mailto:vizionidigjital@gmail.com?subject=Rinovim Abonimi - ${profile?.displayName}&body=Përshëndetje, dëshiroj të rinovoj abonimin tim për planin ${profile?.plan}.`}
            className="flex-grow btn-primary py-2 rounded-lg font-bold text-xs text-center"
          >
            {subT.renewNow}
          </a>
        </div>
      </div>
    </motion.div>
  );
};

const ProfilePage = ({ profile }: { profile: UserProfile | null }) => {
  const { t } = useTranslation();
  const [userProjects, setUserProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const navigate = useNavigate();
  const profileT = t('profile', { returnObjects: true }) as any;

  useEffect(() => {
    if (profile) {
      const q = query(collection(db, 'projects'), where('ownerId', '==', profile.uid));
      const unsubProjects = onSnapshot(q, (snapshot) => {
        setUserProjects(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Project)));
      });
      setLoading(false);
      return () => unsubProjects();
    } else {
      setLoading(false);
    }
  }, [profile]);

  const handleDeleteProject = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'projects', id));
      setConfirmDelete(null);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Zap className="animate-pulse text-primary-light" size={48} /></div>;
  if (!profile) return <div className="min-h-screen flex items-center justify-center">{t('common.loginToView')}</div>;

  return (
    <section className="pt-32 pb-24 px-6 min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 z-0">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary-light/5 rounded-full blur-[100px] pointer-events-none" />
      </div>
      <SEO title={profileT.title} canonical="/profile" noindex={true} />
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="glass-card p-8 rounded-3xl text-center relative overflow-hidden group border border-white/5 hover:border-white/10 transition-colors">
              <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-neon-blue/10 to-transparent opacity-50"></div>
              <div className="relative">
                <div className="w-28 h-28 mx-auto mb-6 relative">
                  <div className="absolute inset-0 bg-primary-light rounded-full blur-[20px] opacity-20 group-hover:opacity-40 transition-opacity"></div>
                  <img src={profile?.photoURL} className="w-full h-full rounded-full object-cover border-2 border-primary-light relative z-10 p-1 bg-near-black" alt="" loading="lazy" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-1 tracking-tight">{profile?.displayName}</h3>
                <p className="text-white/80 text-sm mb-6">{profile?.email}</p>
                <div className="bg-white/5 border border-white/5 rounded-2xl p-4 mb-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-1 h-full bg-primary-light" />
                  <p className="text-[10px] uppercase tracking-widest font-bold text-white/80 mb-1">{profileT.status}</p>
                  <p className="text-xl font-bold capitalize text-primary-light tracking-wide">{profile?.plan}</p>
                </div>
                <button onClick={() => signOut(auth)} className="w-full py-3 rounded-xl text-xs font-bold bg-white/5 hover:bg-red-500/10 text-white/80 hover:text-red-500 border border-white/5 hover:border-red-500/20 transition-all uppercase tracking-widest flex items-center justify-center gap-2">
                  <LogOut size={16} /> {profileT.logout}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 xl:col-span-9 space-y-8">
            <div className="glass-card p-8 rounded-3xl border border-white/5">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 pb-6 border-b border-white/5">
                <div>
                  <h3 className="text-3xl font-display font-bold">{profileT.myProjects}</h3>
                  <p className="text-white/80 mt-1">Shiko dhe menaxho projektet e tua</p>
                </div>
                <button onClick={() => navigate('/publish')} className="btn-primary px-6 py-3 rounded-full text-sm font-bold flex items-center gap-2 hover:shadow-[0_0_25px_rgba(139,92,246,0.3)] transition-all">
                  <Plus size={16} /> {profileT.publishProject}
                </button>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {userProjects.map(p => (
                  <div key={p.id} className="bg-near-black/50 rounded-2xl p-4 border border-white/10 hover:border-primary-light/30 transition-colors group relative flex flex-col">
                    <div className="aspect-video rounded-xl overflow-hidden mb-4 relative bg-charcoal">
                      <img src={p.thumbnail} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt="" loading="lazy" />
                      <div className="absolute inset-0 bg-near-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3 backdrop-blur-sm">
                        <a 
                          href={p.url} 
                          target="_blank" 
                          rel="noreferrer"
                          className="w-10 h-10 bg-white/10 rounded-full hover:bg-primary hover:text-white flex items-center justify-center transition-all text-white"
                          title="Shiko Uebfaqen"
                        >
                          <ExternalLink size={18} />
                        </a>
                        <button 
                          onClick={() => setConfirmDelete(p.id)}
                          className="w-10 h-10 bg-white/10 rounded-full hover:bg-red-500 flex items-center justify-center transition-all text-white"
                          title="Fshij Projektin"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="px-1 flex-grow flex flex-col justify-between">
                      <div>
                        <h4 className="font-bold text-lg mb-1 truncate">{p.title}</h4>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <span className="uppercase tracking-widest px-2 py-1 bg-white/5 text-white/80 text-[10px] font-bold rounded-md">{p.category}</span>
                        <div className="flex items-center gap-3 text-xs font-bold text-white/80">
                          <span className="flex items-center gap-1" title="Pëlqime"><Heart size={14} /> {p.likes?.length || 0}</span>
                          <span className="flex items-center gap-1" title="Shikime"><Eye size={14} /> {p.views || 0}</span>
                        </div>
                      </div>
                    </div>
                    
                    {confirmDelete === p.id && (
                      <div className="absolute inset-0 bg-near-black/95 backdrop-blur-md rounded-2xl flex flex-col justify-center items-center p-6 text-center z-20">
                        <p className="font-bold mb-4">{profileT.deleteConfirm || 'Jeni i sigurt?'}</p>
                        <div className="flex gap-4 w-full">
                          <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2 bg-white/10 rounded-lg text-sm font-bold hover:bg-white/20 transition-colors">{profileT.cancel || 'Anulo'}</button>
                          <button onClick={() => handleDeleteProject(p.id)} className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors">{profileT.yesDelete || 'Fshij'}</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {userProjects.length === 0 && (
                  <div className="col-span-full py-12 flex flex-col items-center justify-center text-center bg-white/5 rounded-2xl border border-white/5 border-dashed">
                    <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4 text-white/80">
                      <Plus size={24} />
                    </div>
                    <p className="text-white/80 mb-4">{profileT.noProjects}</p>
                    <button onClick={() => navigate('/publish')} className="text-primary-light font-bold text-sm hover:underline">
                      Krijo projektin e parë
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="glass-card p-8 rounded-3xl">
              <h3 className="text-2xl font-bold mb-6">{profileT.subDetails}</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-white/5">
                    <span className="text-white/80">{profileT.status}</span>
                    <span className={`font-bold ${(!profile?.planExpiresAt || profile.planExpiresAt.toMillis() > Date.now()) && profile?.status !== 'inactive' ? 'text-green-400' : 'text-red-400'}`}>
                      {(!profile?.planExpiresAt || profile.planExpiresAt.toMillis() > Date.now()) && profile?.status !== 'inactive' ? profileT.active : profileT.inactive}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-white/5">
                    <span className="text-white/80">{profileT.expires}</span>
                    <span className="font-bold">{profile?.planExpiresAt?.toDate?.() ? profile.planExpiresAt.toDate().toLocaleDateString() : profileT.noExpiry}</span>
                  </div>
                </div>
                <div className="bg-white/5 rounded-2xl p-6">
                  <h4 className="font-bold mb-2">{profileT.wantMore}</h4>
                  <p className="text-sm text-white/80 mb-4">{profileT.upgradeSub}</p>
                  <button onClick={() => navigate('/')} className="text-primary-light font-bold text-sm">{profileT.viewPlans} →</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PublishPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ title: '', url: '', description: '', category: 'web' as const, projectType: 'live' as 'live' | 'demo' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const publishT = t('publish', { returnObjects: true }) as any;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) return;
    setLoading(true);
    
    const thumbnail = `https://s.wordpress.com/mshots/v1/${encodeURIComponent(formData.url)}?w=600&h=450`;
    const seoScore = calculateSeoScore({ ...formData, thumbnail });

    try {
      await addDoc(collection(db, 'projects'), {
        ...formData,
        thumbnail,
        seoScore,
        ownerId: auth.currentUser.uid,
        ownerName: auth.currentUser.displayName,
        likes: [],
        views: 0,
        createdAt: serverTimestamp()
      });
      navigate('/profile');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="pt-32 pb-24 px-6 min-h-screen flex items-center justify-center">
      <SEO title={publishT.title} canonical="/publish" noindex={true} />
      <div className="max-w-xl w-full glass-card p-8 md:p-12 rounded-3xl">
        <h2 className="text-3xl font-bold mb-8">{publishT.title} <span className="text-primary-light">{publishT.titleAccent}</span></h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-white/80">{publishT.projectTitle}</label>
            <input required placeholder={publishT.placeholderTitle} className="w-full bg-white/5 border border-white/10 rounded-xl p-4 outline-none focus:border-primary-light" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-white/80">{publishT.url}</label>
            <input required type="url" placeholder={publishT.placeholderUrl} className="w-full bg-white/5 border border-white/10 rounded-xl p-4 outline-none focus:border-primary-light" value={formData.url} onChange={e => setFormData({...formData, url: e.target.value})} />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-white/80">{publishT.category}</label>
            <select className="w-full bg-white/5 border border-white/10 rounded-xl p-4 outline-none focus:border-primary-light text-white/80" value={formData.category} onChange={e => setFormData({...formData, category: e.target.value as any})}>
              <option value="web">Web Design</option>
              <option value="mobile">Mobile App</option>
              <option value="ai">AI Solution</option>
              <option value="restaurant">Restaurant/Cafe</option>
              <option value="ecommerce">E-Commerce</option>
              <option value="business">Business</option>
              <option value="portfolio">Portfolio</option>
              <option value="templates">Templates</option>
              <option value="branding">Branding</option>
              <option value="design">Design</option>
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-white/80">{publishT.description}</label>
            <textarea required rows={4} className="w-full bg-white/5 border border-white/10 rounded-xl p-4 outline-none focus:border-primary-light resize-none" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
          </div>
          <button disabled={loading} className="w-full btn-primary py-4 rounded-xl font-bold hover:shadow-[0_0_25px_rgba(139,92,246,0.4)] transition-all disabled:opacity-50">
            {loading ? publishT.submitting : publishT.submit}
          </button>
        </form>
      </div>
    </section>
  );
};


const LegacyArticleRedirect = ({ targetLang = 'en' }: { targetLang?: 'sq' | 'en' | 'de' }) => {
  const { title } = useParams<{ title?: string }>();
  const location = useLocation();
  const raw = title || location.pathname.replace(/^\/(?:articles|artikujt|artikel)\/?/, '');
  const matched = findArticle(raw);
  if (matched) {
    return <Navigate to={getCanonicalArticlePath(matched.id, targetLang)} replace />;
  }
  return <Navigate to={targetLang === 'sq' ? '/sq/blog' : targetLang === 'de' ? '/de/blog' : '/en/blog'} replace />;
};

const LanguageWrapper = ({ lang }: { lang: string }) => {
  const { i18n } = useTranslation();
  if (i18n.language !== lang && ['sq', 'en', 'de'].includes(lang)) {
    i18n.changeLanguage(lang);
  }
  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);
  return <Outlet />;
};

const AppContent = () => {
  const { t, i18n } = useTranslation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const location = useLocation();
  const footerT = t('footer', { returnObjects: true }) as any;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setProfile(docSnap.data() as UserProfile);
        } else {
          const newProfile: UserProfile = {
            uid: user.uid,
            email: user.email || '',
            displayName: user.displayName || 'Përdorues',
            photoURL: user.photoURL || '',
            role: 'client',
            plan: 'free',
            createdAt: serverTimestamp() as Timestamp
          };
          await setDoc(docRef, newProfile);
          setProfile(newProfile);
          
          // Send automatic welcome email
          if (newProfile.email) {
            try {
              fetch('/api/email/welcome', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: newProfile.email, displayName: newProfile.displayName })
              }).catch(console.error);
            } catch (e) {
              console.error('Failed to trigger welcome email', e);
            }
          }
        }
      } else {
        setProfile(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
      <div className="font-sans text-white selection:bg-primary-light selection:text-white min-h-screen">
        <ScrollProgress />
        <Navbar />
        
        <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 border-primary-light border-t-transparent animate-spin"></div></div>}>
            <Routes>
              {Object.keys(localizedRoutesMap).map((lang) => {
                const paths = localizedRoutesMap[lang];
                return (
                  <React.Fragment key={lang}><Route element={<LanguageWrapper lang={lang} />}>
                    <Route path={paths.home} element={<HomePage setIsChatOpen={setIsChatOpen} setSelectedPlan={setSelectedPlan} />} />
                    <Route path={paths.services} element={<Services />} />
                    <Route path={paths.webDesign} element={<WebDesign />} />
                    <Route path={paths.websiteDevelopment} element={<WebsiteDevelopment />} />
                    <Route path={paths.businessWebsites} element={<BusinessWebsites />} />
                    <Route path={paths.ecommerce} element={<Ecommerce />} />
                    <Route path={paths.seo} element={<SEOPage />} />
                <Route path={paths.localSeo} element={<LocalSEO />} />
                <Route path={paths.softwareDevelopment} element={<SoftwareDevelopment />} />
                <Route path={paths.branding} element={<Branding />} />
                <Route path={paths.aiSolutions} element={<AiSolutions />} />

                    <Route path={paths.webApplications} element={<WebApplications />} />
                    <Route path={paths.about} element={<AboutUs />} />
                    <Route path={paths.work} element={<Work setIsChatOpen={setIsChatOpen} />} />
                    <Route path={paths.contact} element={<Contact />} />
                    <Route path={paths.templates} element={<TrackerPage />} />
                    <Route path={paths.pricing} element={<PricingPage setIsChatOpen={setIsChatOpen} setSelectedPlan={setSelectedPlan} />} />
                    <Route path={paths.articles} element={<ArticlePage />} />
                    <Route path={paths.blog} element={<Blog />} />
                    <Route path={paths.shabllone} element={<TrackerPage />} />
                    <Route path={paths.admin} element={<AdminDashboard />} />
                    <Route path={paths.payment} element={<PaymentPage plan={selectedPlan} setIsChatOpen={setIsChatOpen} />} />
                    <Route path={paths.profile} element={<ProfilePage profile={profile} />} />
                    <Route path={paths.publish} element={<PublishPage />} />
                    <Route path={paths.privacy} element={<LegalPage title={t('legal.privacy.title')} content={t('legal.privacy.content')} type="privacy" />} />
                    <Route path={paths.terms} element={<LegalPage title={t('legal.terms.title')} content={t('legal.terms.content')} type="terms" />} />
                    <Route path={paths.refund} element={<LegalPage title={t('legal.refund.title')} content={t('legal.refund.content')} type="refund" />} />
                    <Route path={paths.success} element={<SuccessPage />} />
                    <Route path={paths.cancel} element={<CancelPage />} />
                  </Route></React.Fragment>
                );
              })}
                            {/* Fallbacks */}
              <Route path="/articles/:title" element={<LegacyArticleRedirect targetLang="en" />} />
              <Route path="/articles" element={<Navigate to="/en/blog" replace />} />
              <Route path="/artikujt/:title" element={<LegacyArticleRedirect targetLang="sq" />} />
              <Route path="/artikujt" element={<Navigate to="/sq/blog" replace />} />
              <Route path="/artikel/:title" element={<LegacyArticleRedirect targetLang="de" />} />
              <Route path="/artikel" element={<Navigate to="/de/blog" replace />} />

              <Route path="/pricing" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.pricing || '/sq/cmimet'} replace />} />
              <Route path="/services" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.services || '/sq/sherbimet'} replace />} />
              <Route path="/about" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.about || '/sq/rreth-nesh'} replace />} />
              <Route path="/contact" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.contact || '/sq/kontakt'} replace />} />
              <Route path="/work" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.work || '/sq/puna'} replace />} />
              <Route path="/templates" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.templates || '/sq/shabllone'} replace />} />
              <Route path="/admin" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.admin || '/sq/admin'} replace />} />
              <Route path="/privacy" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.privacy || '/sq/privatesia'} replace />} />
              <Route path="/terms" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.terms || '/sq/kushtet'} replace />} />
              <Route path="/refund" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.refund || '/sq/rimbursim'} replace />} />
              <Route path="/profile" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.profile || '/sq/profili'} replace />} />
              <Route path="/payment" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.payment || '/sq/pagesa'} replace />} />
              <Route path="/blog" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.blog || '/sq/blog'} replace />} />
              <Route path="/success" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.success || '/sq/sukses'} replace />} />
              <Route path="/cancel" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.cancel || '/sq/anulo'} replace />} />
              <Route path="/publish" element={<Navigate to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.publish || '/sq/publiko'} replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </motion.div>
      </AnimatePresence>

      <footer className="bg-near-black border-t border-white/5 pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-12 mb-20">
            <div>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.home || '/'} className="flex items-center gap-2 mb-4 cursor-pointer">
                <Logo className="w-8 h-8" />
                <span className="font-display font-bold text-xl tracking-tighter">VIZIONI<span className="text-primary-light">DIGJITAL</span></span>
              </Link>
              <p className="text-white/80 mb-6 text-sm max-w-sm leading-relaxed">{footerT.desc || 'We build digital solutions that empower businesses.'}</p>
              <div className="mb-4">
                <p className="text-white/80 text-xs uppercase tracking-widest font-bold mb-3">{footerT.followUs || 'Follow us on social media:'}</p>
                <div className="flex items-center gap-4 text-white/80">
                   <a href="https://www.facebook.com/profile.php?id=61588797729975" aria-label="Facebook" target="_blank" rel="noreferrer" className="hover:text-primary-light transition-colors"><Facebook size={20} /></a>
                   <a href="https://www.instagram.com/vizionidigjital/" aria-label="Instagram" target="_blank" rel="noreferrer" className="hover:text-primary-light transition-colors"><Instagram size={20} /></a>
                   <a href="https://www.tiktok.com/@vizioni.digjital" aria-label="TikTok" target="_blank" rel="noreferrer" className="hover:text-primary-light transition-colors"><Video size={20} /></a>
                </div>
              </div>
            </div>
            <div>
              <h2 className="font-bold mb-6">{t('nav.contact')}</h2>
              <div className="space-y-4 text-white/80">
                <p className="flex items-center gap-2"><MapPin size={18} /> {footerT.address}</p>
                <p className="flex items-center gap-2"><Phone size={18} /> {footerT.phone}</p>
                <p className="flex items-center gap-2"><Mail size={18} /> vizionidigjital@gmail.com</p>
              </div>
            </div>
            <div className="glass-card p-8 rounded-3xl border border-white/5 w-full">
              <h2 className="font-bold mb-2">{footerT.newsletter}</h2>
              <p className="text-xs text-white/80 mb-6">{footerT.newsSub}</p>
              <form className="flex md:flex-row flex-col gap-2" onSubmit={async (e) => { 
                e.preventDefault(); 
                const email = (e.currentTarget.elements[0] as HTMLInputElement).value;
                try {
                  await addDoc(collection(db, 'subscribers'), {
                    email,
                    createdAt: serverTimestamp(),
                    status: 'active'
                  });

                  // Notification API
                  fetch('/api/email/newsletter-signup', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                  }).catch(e => console.error('Subscription notification failed:', e));

                  alert(i18n.language.startsWith('sq') ? 'Faleminderit për abonimin!' : 'Thank you for subscribing!');
                  (e.target as HTMLFormElement).reset();
                } catch (err) {
                  console.error('Newsletter error:', err);
                  alert(i18n.language.startsWith('sq') ? 'Pati një gabim. Ju lutem provoni përsëri.' : 'An error occurred. Please try again.');
                }
              }}>
                <input required type="email" placeholder={footerT.emailPlaceholder} className="flex-grow bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-primary-light" />
                <button className="btn-primary px-6 py-3 rounded-xl font-bold text-sm hover:shadow-[0_0_25px_rgba(139,92,246,0.3)] transition-all">OK</button>
              </form>
            </div>
          </div>
          
          <div className="text-center pt-10 border-t border-white/5">
            <p className="text-white/80 text-xs">{footerT.rights}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-4 md:gap-8">
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.services || "/sq/sherbimet"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('nav.services')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.work || "/sq/puna"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('nav.work')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.templates || "/sq/shabllone"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('nav.tracker')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.pricing || "/sq/cmimet"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('nav.pricing')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.about || "/sq/rreth-nesh"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('nav.about')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.contact || "/sq/kontakt"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('nav.contact')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.privacy || "/sq/privatesia"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('legal.privacy.title')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.terms || "/sq/kushtet"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('legal.terms.title')}</Link>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.refund || "/sq/rimbursim"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('legal.refund.title')}</Link>
              <a href="/sitemap.xml" className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">Sitemap</a>
              <Link to={localizedRoutesMap[i18n.language.split('-')[0] || 'sq']?.admin || "/sq/admin"} className="text-[10px] uppercase tracking-widest text-white/80 hover:text-primary-light transition-colors">{t('nav.admin')}</Link>
            </div>
          </div>
        </div>
      </footer>

      <SubscriptionNotification profile={profile} />
      <LeadMagnetBanner />
      <ChatSystem isOpen={isChatOpen} setIsOpen={setIsChatOpen} profile={profile} />
    </div>
  );
};

const NotFound = () => {
  return (
    <div className="min-h-screen flex items-center justify-center text-center px-6 pt-32 pb-24">
      <SEO title="404 Not Found" noindex={true} />
      <div>
        <h1 className="text-6xl font-bold text-primary-light mb-4">404</h1>
        <h2 className="text-3xl font-bold text-white mb-6">Page Not Found</h2>
        <p className="text-white/70 mb-8 max-w-md mx-auto">The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.</p>
        <Link to="/" className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-bold transition-all">Return Home</Link>
      </div>
    </div>
  );
};

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
          <StarBackground />
          <AppContent />
      </BrowserRouter>
    </HelmetProvider>
  );
}
